import { clear, el } from '../../../core/dom.js';
import type { RescueModule, RescueResult } from '../../rescue-types.js';
import { minigameState } from '../../../storage/db.js';
import {
  ParticleSystem, hexToABGR, hslToABGR,
  TYPE_SHIMMER, TYPE_TRAIL,
} from './particle-system.js';
import { ChunkPool } from './chunk-pool.js';
import {
  INTENSITY_LOW, INTENSITY_MID, INTENSITY_HIGH,
} from './cell-atlas.js';
import {
  type Persisted, type AchievementDef,
  defaultsPersisted, buildHighScoreEntry, insertHighScore,
  formatScoreDate, formatDuration, evaluateAchievements,
  addXp, xpForLevel, ACHIEVEMENTS,
} from './progression.js';
import { SoundEngine } from './audio.js';
import {
  type DirectorState, type Mission, type ExcitingEventKind,
  makeDirector, rollMission,
} from './director.js';
import { PHRASES, pickPhrase } from './phrases.js';
import './infinity-breaker.css';

const MODULE_ID = 'infinity-breaker';
const WORLD_W = 360;
const WORLD_H = 640;
const HUD_H = 56;
const PADDLE_Y = 540;
const PADDLE_BASE_W = 70;
const PADDLE_H = 14;
const BALL_R = 6;
const INITIAL_BALL_SPEED = 340;
const MAX_BALL_SPEED = 620;
const UPGRADE_MIN_WAVES = 2;
const UPGRADE_MIN_GAP_SEC = 35;
const FIRST_UPGRADE_SEC = 30;
const ROWS = 7;
const COLS = 8;
const BRICK_GAP = 4;
const BRICK_TOP = 80;
const BRICK_W = (WORLD_W - (COLS + 1) * BRICK_GAP) / COLS;
const BRICK_H = 22;
const COMBO_TIMEOUT_MS = 2000;
const COMBO_MULT_CAP_COUNT = 60; // combo above this stops boosting multiplier
const MOMENTUM_TARGET = 1;
const PASSIVE_MOMENTUM_PER_SEC = 0.0008;
const BRICK_MOMENTUM = 0.0055;
const WAVE_CLEAR_MOMENTUM = 0.04;
const SHIELDS_START = 3;
const MAX_BALLS = 5;
const MAX_FALLING_PICKUPS = 2;
const PICKUP_COOLDOWN_SEC = 2.0;
const PICKUP_FALL = 120;
const PICKUP_BASE_CHANCE = 0.08;
const PICKUP_CHANCE_CAP = 0.22;
const GUARANTEED_FIRST_PICKUP_SEC = 8;
const STEP_SEC = 1 / 120;
const MAX_FRAME_TIME = 0.08;
const SESSION_TARGET_SEC = 240;
const OVERDRIVE_DURATION_SEC = 10;
const NEAR_MISS_COOLDOWN_SEC = 18;
const NEAR_MISS_REACH = 22;
const PERFECT_CENTER_ABS = 0.18;
const PERFECT_EDGE_LO = 0.78;
const PERFECT_EDGE_HI = 0.96;
const DRY_SPELL_SMALL_SEC = 3.5;
const DRY_SPELL_MAJOR_SEC = 32;
const MISSION_INTERVAL_SEC = 14;
const MAX_FLOATING_TEXTS = 18;
// Pool capacity for the SoA particle system. Sized so chaotic late-wave bursts
// stay under the cap; the renderer is single-pixel ImageData so per-particle
// cost is tiny — scale up if you want denser shatter on huge displays.
const PARTICLE_CAP = 300_000;

// Pre-packed ABGR particle colors — avoid string parsing in hot paths.
const COLOR_WHITE = hexToABGR('#ffffff');
const COLOR_TEAL = hexToABGR('#5eead4');
const COLOR_AMBER = hexToABGR('#fbbf24');
const COLOR_CORAL = hexToABGR('#f87171');
const AMBIENT_PALETTE = [
  hexToABGR('#5eead4'), hexToABGR('#67e8f9'),
  hexToABGR('#a78bfa'), hexToABGR('#fbbf24'),
];
const CLEAR_PALETTE = [
  hexToABGR('#5eead4'), hexToABGR('#67e8f9'), hexToABGR('#fbbf24'),
  hexToABGR('#a78bfa'), hexToABGR('#f472b6'),
];

// Memoize hex / hsl string → ABGR. Brick hue strings are bounded (one per hue
// step) and pickup colors are constants — cache hit rate is effectively 100%.
const _colorCache = new Map<string, number>();
function colorU32(s: string): number {
  const hit = _colorCache.get(s);
  if (hit !== undefined) return hit;
  let v: number;
  if (s.startsWith('#')) {
    v = hexToABGR(s);
  } else if (s.startsWith('hsl')) {
    const m = /hsl\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)%\s*,\s*(-?\d+(?:\.\d+)?)%\s*\)/.exec(s);
    v = m ? hslToABGR(parseFloat(m[1]!), parseFloat(m[2]!), parseFloat(m[3]!)) : 0xffffffff;
  } else {
    v = 0xffffffff;
  }
  _colorCache.set(s, v);
  return v;
}
function brickHueU32(hue: number): number {
  return colorU32(`hsl(${hue}, 80%, 62%)`);
}

// Warp grid (geometry-wars style)
const GRID_COLS = 22;
const GRID_ROWS = 38;
const GRID_BALL_PULL = 320;
const GRID_WELL_PULL = 4200;
const GRID_MAX_DISP = 18;

interface Vec { x: number; y: number }

type BrickType =
  | 'normal' | 'reinforced' | 'glass' | 'bomb' | 'bounty'
  | 'crystal' | 'mirror' | 'boss';
type WaveAffix =
  | 'none' | 'comet_rain' | 'glass_garden' | 'bomb_bloom'
  | 'moving_row' | 'star_rush';
interface Brick {
  col: number;
  row: number;
  x: number; y: number; w: number; h: number;
  type: BrickType;
  hp: number;
  maxHp: number;
  destroyed: boolean;
  hitFlash: number; // 0..1 timer
  hue: number;
}

interface Ball {
  p: Vec;
  v: Vec;
  alive: boolean;
  trail: Array<{ x: number; y: number; t: number }>;
}

interface Paddle {
  cx: number;
  prevCx: number;
  width: number;
  baseWidth: number;
  targetX: number;
  vx: number;
  glow: number; // 0..1
  charge: number; // 0..1 — fills on perfect hits, fires pulse at 1
  perfectStreak: number;
  lastPerfectAtSec: number;
}

type PickupKind =
  | 'wide' | 'multi' | 'shield' | 'slow' | 'score2x' | 'momentum'
  | 'fireball' | 'bomb_seed' | 'laser' | 'magnet' | 'safe'
  | 'prism_split' | 'chain_lightning';
interface Pickup {
  p: Vec;
  v: Vec;
  kind: PickupKind;
  alive: boolean;
  age: number;
}

type TextKind =
  | 'score'
  | 'combo'
  | 'perfect'
  | 'pickup'
  | 'mission'
  | 'save'
  | 'overdrive'
  | 'achievement'
  | 'high_score'
  | 'session';

interface Rect { x: number; y: number; w: number; h: number }
interface CanvasButton {
  id: string;
  rect: Rect;
  label: string;
  sublabel: string;
  tone: 'primary' | 'ghost' | 'gold' | 'danger';
  onClick: () => void;
}
type CanvasMenuKind = 'start' | 'pause';
interface CanvasMenu {
  kind: CanvasMenuKind;
  title: string;
  subtitle: string;
  enteredAtSec: number;
  buttons: CanvasButton[];
  info: string[];
}

interface FloatingText {
  p: Vec; vy: number; life: number; maxLife: number;
  text: string;
  color: string;
  scale: number;
  kind: TextKind;
  priority: number;
}

type UpgradeId =
  | 'wider_base'
  | 'momentum_carry'
  | 'chain_pop'
  | 'lucky_drops'
  | 'combo_insurance'
  | 'extra_ball'
  | 'score_surge'
  | 'shield_charm'
  | 'comet_core'
  | 'shield_bloom'
  | 'bounty_hunter'
  | 'paddle_cannon'
  | 'magnet_hands'
  | 'time_thread';

interface UpgradeDef {
  id: UpgradeId;
  name: string;
  desc: string;
  apply: (game: Game) => void;
  oneShot?: boolean;
  /** Repeatable — may appear again even after it has been picked. */
  stackable?: boolean;
  /** Optional gate: skip when condition fails (e.g. cap reached). */
  canOffer?: (game: Game) => boolean;
}

/** Optional hit info threaded into damageBrick → spawnBrickBurst for cell fracture. */
interface BrickHit {
  fromExplosion?: boolean;
  impactX?: number;
  impactY?: number;
  impactVx?: number;
  impactVy?: number;
}

interface Shockwave {
  p: Vec;
  r: number;
  maxR: number;
  life: number;
  maxLife: number;
  color: string;
}

interface GridWell {
  p: Vec;
  strength: number;
  life: number;
  maxLife: number;
}

interface Game {
  phase: 'serving' | 'playing' | 'paused' | 'upgrade_choice' | 'game_over';
  wave: number;
  score: number;
  combo: number;
  comboTimerMs: number;
  momentum: number;
  shields: number;
  bricks: Brick[];
  balls: Ball[];
  pickups: Pickup[];
  ps: ParticleSystem;
  chunks: ChunkPool;
  texts: FloatingText[];
  shockwaves: Shockwave[];
  wells: GridWell[];
  paddle: Paddle;
  bricksSinceDrop: number;
  // Timed power-ups
  wideUntilSec: number;
  score2xUntilSec: number;
  fireballUntilSec: number;
  magnetUntilSec: number;
  chainLightningHits: number;
  prismSplitUntilSec: number;
  safeBallReady: boolean;
  // Run upgrades
  upgrades: Set<UpgradeId>;
  scoreMultBonus: number; // permanent score boost
  comboInsurance: boolean; // skip next combo reset
  pickupChanceBonus: number;
  extraBallPerWave: boolean;
  chainPopChance: number;
  flags: {
    paddleCannon: boolean;
    shieldBloom: boolean;
    bountyBricks: boolean;
    magnetHands: boolean;
    timeThread: boolean;
  };
  cometEvery: number; // 0 = disabled; otherwise every N hits pierces
  cometCounter: number;
  // Wave variety
  waveAffix: WaveAffix;
  affixTick: number;
  movingRowOffset: number;
  movingRowIndex: number;
  bossActive: boolean;
  // Effects
  shake: { x: number; y: number; mag: number; life: number };
  flashAlpha: number;
  /** When set and we're 'serving', auto-launch after this game.elapsedSec. */
  serveCountdownUntilSec: number | null;
  // Canvas menu (start / pause)
  menu: CanvasMenu | null;
  // Director + mission
  director: DirectorState;
  mission: Mission | null;
  // Overdrive
  overdriveUntilSec: number;
  overdriveCount: number;
  overdriveLevel: number;
  // Session arc
  sessionComplete: boolean;
  sessionCompletedAtSec: number | null;
  // Run-end persistence guard
  runRecorded: boolean;
  runStarted: boolean;
  lastRunRank: number | null;
  // Audio
  audio: SoundEngine;
  // XP this run (carried into persistRunEnd)
  xpEarned: number;
  levelsGained: number;
  // Stats
  elapsedSec: number;
  bricksBroken: number;
  bountyBricksBroken: number;
  bossesDefeated: number;
  perfectHits: number;
  shieldSaves: number;
  missionsCompleted: number;
  maxComboSeen: number;
  momentumMilestones: number;
  lastUpgradeAtWave: number;
  lastUpgradeAtSec: number;
  lastPickupAtSec: number;
  lastNearMissSaveAtSec: number;
  firstPickupSpawned: boolean;
  firstUpgradeOffered: boolean;
  lastShieldLossAtSec: number;
  newlyUnlocked: AchievementDef[];
}

export const infinityBreaker: RescueModule = {
  id: MODULE_ID,
  name: 'Infinity Breaker',
  kind: 'minigame',
  defaultDurationSec: 240,
  reducedMotion: false,
  safety: {
    gamblingLike: false,
    excludeForDomains: [],
    experimental: false,
    reducedMotionSafe: false,
  },
  async run(host) {
    return new Promise<RescueResult>((resolve) => { void mount(host, resolve); });
  },
};

async function mount(host: HTMLElement, resolve: (r: RescueResult) => void) {
  clear(host);
  document.body.classList.add('ib-active');
  const persisted: Persisted = { ...defaultsPersisted(), ...(await minigameState.get<Persisted>(MODULE_ID) ?? {}) };

  const stage = el('div', { class: 'ib-stage' });
  const hud = el('div', { class: 'ib-hud' });
  const scoreEl = el('div', { class: 'ib-stat' },
    el('div', { class: 'ib-stat-label' }, 'Score'),
    el('div', { class: 'ib-stat-value', dataset: { role: 'score' } }, '0'),
  );
  const waveEl = el('div', { class: 'ib-stat' },
    el('div', { class: 'ib-stat-label' }, 'Wave'),
    el('div', { class: 'ib-stat-value', dataset: { role: 'wave' } }, '1'),
  );
  const comboEl = el('div', { class: 'ib-stat ib-combo' },
    el('div', { class: 'ib-stat-label' }, 'Combo'),
    el('div', { class: 'ib-stat-value', dataset: { role: 'combo' } }, '0'),
  );
  const shieldEl = el('div', { class: 'ib-stat' },
    el('div', { class: 'ib-stat-label' }, 'Shields'),
    el('div', { class: 'ib-stat-value', dataset: { role: 'shields' } }, String(SHIELDS_START)),
  );
  const closeBtn = el('button', {
    class: 'ib-close',
    attrs: { 'aria-label': 'Quit Infinity Breaker' },
    onclick: () => quit(),
  }, '×');
  hud.appendChild(scoreEl);
  hud.appendChild(waveEl);
  hud.appendChild(comboEl);
  hud.appendChild(shieldEl);
  hud.appendChild(closeBtn);

  const momentumWrap = el('div', { class: 'ib-momentum' },
    el('div', { class: 'ib-momentum-fill', dataset: { role: 'momentum' } }),
  );

  const canvas = document.createElement('canvas');
  canvas.className = 'ib-canvas';
  canvas.style.touchAction = 'none';
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    document.body.classList.remove('ib-active');
    resolve({ kind: 'abandoned', details: { reason: 'no-canvas' } });
    return;
  }

  const overlay = el('div', { class: 'ib-overlay', dataset: { role: 'overlay' } });

  stage.appendChild(hud);
  stage.appendChild(momentumWrap);
  stage.appendChild(canvas);
  stage.appendChild(overlay);
  host.appendChild(stage);

  // ---- World scale ----
  let scale = 1;
  let offsetX = 0;
  // Particle layer: an offscreen canvas at device-pixel resolution whose
  // backing buffer we plot single-pixel particles into via a Uint32 view of
  // ImageData. We composite it into the main canvas with 'lighter' so dense
  // sparks/embers add to existing glow instead of overwriting it.
  const offCanvas = document.createElement('canvas');
  const offCtx = offCanvas.getContext('2d', { willReadFrequently: false });
  if (!offCtx) {
    document.body.classList.remove('ib-active');
    resolve({ kind: 'abandoned', details: { reason: 'no-canvas' } });
    return;
  }
  let particleImage: ImageData | null = null;
  let particleBuf: Uint32Array | null = null;
  let particleDpr = 1;
  function fitCanvas(): void {
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    const cssW = rect.width;
    const cssH = rect.height;
    // Layout race: ResizeObserver can fire with 0×0 before the stage is sized.
    // Skip until we have a real rect — game state is fine to keep ticking.
    if (cssW < 1 || cssH < 1) return;
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
    const sx = cssW / WORLD_W;
    const sy = cssH / WORLD_H;
    scale = Math.min(sx, sy);
    const drawnW = WORLD_W * scale;
    offsetX = (cssW - drawnW) / 2;
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    offCanvas.width = canvas.width;
    offCanvas.height = canvas.height;
    particleImage = offCtx!.createImageData(offCanvas.width, offCanvas.height);
    particleBuf = new Uint32Array(particleImage.data.buffer);
    particleDpr = dpr;
  }
  fitCanvas();
  const ro = new ResizeObserver(fitCanvas);
  ro.observe(canvas);
  window.addEventListener('orientationchange', fitCanvas);

  function clientToWorldX(clientX: number): number {
    const rect = canvas.getBoundingClientRect();
    const localX = clientX - rect.left - offsetX;
    return Math.max(0, Math.min(WORLD_W, localX / scale));
  }
  function clientToWorld(clientX: number, clientY: number): Vec {
    const rect = canvas.getBoundingClientRect();
    const drawnH = WORLD_H * scale;
    const offsetY = (rect.height - drawnH) / 2;
    const localX = clientX - rect.left - offsetX;
    const localY = clientY - rect.top - offsetY;
    return {
      x: clamp(localX / scale, 0, WORLD_W),
      y: clamp(localY / scale, 0, WORLD_H),
    };
  }
  function handleCanvasMenuClick(p: Vec): boolean {
    if (!game.menu) return false;
    for (const b of game.menu.buttons) {
      if (p.x >= b.rect.x && p.x <= b.rect.x + b.rect.w
          && p.y >= b.rect.y && p.y <= b.rect.y + b.rect.h) {
        b.onClick();
        return true;
      }
    }
    return true; // swallow taps on the menu backdrop
  }
  function hideCanvasMenu(): void {
    game.menu = null;
  }
  function showStartCanvasMenu(): void {
    const w = WORLD_W - 48;
    const x = 24;
    const yTop = 220;
    const btnH = 40;
    const gap = 8;
    const row = (i: number): number => yTop + (btnH + gap) * i;
    const buttons: CanvasButton[] = [
      {
        id: 'launch', rect: { x, y: row(0), w, h: btnH },
        label: 'LAUNCH RUN', sublabel: 'Tap to begin',
        tone: 'primary',
        onClick: () => {
          hideCanvasMenu();
          if (game.balls.some(attached)) launchServingBall(game);
          game.phase = 'playing';
        },
      },
      {
        id: 'instructions', rect: { x, y: row(1), w, h: btnH },
        label: 'INSTRUCTIONS', sublabel: 'How to play',
        tone: 'ghost',
        onClick: () => { showInstructionsOverlay(); },
      },
      {
        id: 'achievements', rect: { x, y: row(2), w, h: btnH },
        label: 'ACHIEVEMENTS',
        sublabel: `${persisted.achievements.length}/${ACHIEVEMENTS.length} unlocked`,
        tone: 'ghost',
        onClick: () => { showAchievementsOverlay(); },
      },
      {
        id: 'scores', rect: { x, y: row(3), w, h: btnH },
        label: 'HALL OF SPARKS', sublabel:
          persisted.highScores.length > 0
            ? `Top ${persisted.highScores[0]!.score.toLocaleString()}`
            : 'No runs yet',
        tone: 'ghost',
        onClick: () => { showLeaderboardOverlay(); },
      },
      {
        id: 'quit', rect: { x, y: row(4), w, h: btnH },
        label: 'EXIT', sublabel: 'Leave Infinity Breaker',
        tone: 'danger',
        onClick: () => quit(),
      },
    ];
    game.menu = {
      kind: 'start',
      title: 'INFINITY BREAKER',
      subtitle: 'Ride the wave. Break the storm.',
      enteredAtSec: game.elapsedSec,
      buttons,
      info: [
        `BEST ${persisted.bestScore.toLocaleString()}`,
        `TOP WAVE ${persisted.bestWave}`,
        `LV ${persisted.progression.level}`,
      ],
    };
  }
  function showPauseCanvasMenu(): void {
    const w = WORLD_W - 48;
    const x = 24;
    const yTop = 240;
    const btnH = 46;
    const gap = 10;
    const buttons: CanvasButton[] = [
      {
        id: 'resume', rect: { x, y: yTop, w, h: btnH },
        label: 'RESUME', sublabel: 'Back into it',
        tone: 'primary',
        onClick: () => { hideCanvasMenu(); resume(); },
      },
      {
        id: 'end', rect: { x, y: yTop + btnH + gap, w, h: btnH },
        label: 'END RUN', sublabel: 'Bank this run',
        tone: 'ghost',
        onClick: () => {
          hideCanvasMenu();
          game.phase = 'game_over';
          void persistRunEnd().then(showGameOverOverlay);
        },
      },
      {
        id: 'quit', rect: { x, y: yTop + (btnH + gap) * 2, w, h: btnH },
        label: 'EXIT', sublabel: 'Leave the game',
        tone: 'danger',
        onClick: () => quit(),
      },
    ];
    game.menu = {
      kind: 'pause',
      title: 'STORM PAUSED',
      subtitle: 'Nothing lost. Your run is waiting.',
      enteredAtSec: game.elapsedSec,
      buttons,
      info: [],
    };
  }

  // ---- Game state ----
  const game: Game = {
    phase: 'serving',
    wave: 1,
    score: 0,
    combo: 0,
    comboTimerMs: 0,
    momentum: 0,
    shields: SHIELDS_START,
    bricks: [],
    balls: [],
    pickups: [],
    ps: new ParticleSystem(PARTICLE_CAP),
    chunks: new ChunkPool(2048),
    texts: [],
    shockwaves: [],
    wells: [],
    paddle: {
      cx: WORLD_W / 2,
      prevCx: WORLD_W / 2,
      width: PADDLE_BASE_W,
      baseWidth: PADDLE_BASE_W,
      targetX: WORLD_W / 2,
      vx: 0,
      glow: 0,
      charge: 0,
      perfectStreak: 0,
      lastPerfectAtSec: -100,
    },
    bricksSinceDrop: 0,
    wideUntilSec: 0,
    score2xUntilSec: 0,
    fireballUntilSec: 0,
    magnetUntilSec: 0,
    chainLightningHits: 0,
    prismSplitUntilSec: 0,
    safeBallReady: false,
    upgrades: new Set<UpgradeId>(),
    scoreMultBonus: 0,
    comboInsurance: false,
    pickupChanceBonus: 0,
    extraBallPerWave: false,
    chainPopChance: 0,
    flags: {
      paddleCannon: false,
      shieldBloom: false,
      bountyBricks: false,
      magnetHands: false,
      timeThread: false,
    },
    cometEvery: 0,
    cometCounter: 0,
    waveAffix: 'none',
    affixTick: 0,
    movingRowOffset: 0,
    movingRowIndex: -1,
    bossActive: false,
    shake: { x: 0, y: 0, mag: 0, life: 0 },
    flashAlpha: 0,
    serveCountdownUntilSec: null,
    menu: null,
    director: makeDirector(),
    mission: null,
    overdriveUntilSec: 0,
    overdriveCount: 0,
    overdriveLevel: 0,
    sessionComplete: false,
    sessionCompletedAtSec: null,
    runRecorded: false,
    runStarted: false,
    lastRunRank: null,
    audio: new SoundEngine(persisted.settings),
    xpEarned: 0,
    levelsGained: 0,
    elapsedSec: 0,
    bricksBroken: 0,
    bountyBricksBroken: 0,
    bossesDefeated: 0,
    perfectHits: 0,
    shieldSaves: 0,
    missionsCompleted: 0,
    maxComboSeen: 0,
    momentumMilestones: 0,
    lastUpgradeAtWave: 0,
    lastUpgradeAtSec: -100,
    lastPickupAtSec: -100,
    lastNearMissSaveAtSec: -100,
    firstPickupSpawned: false,
    firstUpgradeOffered: false,
    lastShieldLossAtSec: -100,
    newlyUnlocked: [],
  };

  buildWave(game);
  spawnServeBall(game);
  showStartCanvasMenu();

  // ---- Input ----
  let activePointer: number | null = null;
  let leftHeld = false;
  let rightHeld = false;

  canvas.addEventListener('pointerdown', (e: PointerEvent) => {
    if (game.menu) {
      const p = clientToWorld(e.clientX, e.clientY);
      if (handleCanvasMenuClick(p)) {
        e.preventDefault();
        return;
      }
    }
    if (game.phase === 'paused' || game.phase === 'upgrade_choice' || game.phase === 'game_over') return;
    activePointer = e.pointerId;
    canvas.setPointerCapture(e.pointerId);
    game.paddle.targetX = clientToWorldX(e.clientX);
    if (game.phase === 'serving') {
      game.serveCountdownUntilSec = null;
      launchServingBall(game);
      hideOverlay();
      game.phase = 'playing';
    }
    e.preventDefault();
  });
  canvas.addEventListener('pointermove', (e: PointerEvent) => {
    if (activePointer !== e.pointerId) return;
    game.paddle.targetX = clientToWorldX(e.clientX);
    e.preventDefault();
  });
  const releasePointer = (e: PointerEvent): void => {
    if (activePointer !== e.pointerId) return;
    activePointer = null;
  };
  canvas.addEventListener('pointerup', releasePointer);
  canvas.addEventListener('pointercancel', releasePointer);

  const keyHandler = (e: KeyboardEvent, down: boolean): void => {
    if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') { leftHeld = down; e.preventDefault(); }
    if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') { rightHeld = down; e.preventDefault(); }
    if (down && (e.key === ' ' || e.key === 'Enter')) {
      if (overlay.classList.contains('visible') && game.phase !== 'upgrade_choice' && game.phase !== 'game_over') {
        hideOverlay();
        e.preventDefault();
        return;
      }
      if (game.menu) {
        // Run the first button as the keyboard default.
        const btn = game.menu.buttons[0];
        if (btn) btn.onClick();
      } else if (game.phase === 'serving') {
        game.serveCountdownUntilSec = null;
        launchServingBall(game);
        hideOverlay();
        game.phase = 'playing';
      } else if (game.phase === 'paused') {
        resume();
      } else if (game.phase === 'game_over') {
        quit();
      }
      e.preventDefault();
    }
    if (down && (e.key === 'p' || e.key === 'P')) { togglePause(); e.preventDefault(); }
    if (down && e.key === 'Escape') { togglePause(); e.preventDefault(); }
  };
  const onKeyDown = (e: KeyboardEvent): void => keyHandler(e, true);
  const onKeyUp = (e: KeyboardEvent): void => keyHandler(e, false);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  // ---- Particle layer render ----
  // Rebuilds the offscreen ImageData each frame: clear → plot live particles
  // at device pixels → putImageData. The result gets composited into the main
  // canvas via drawImage with 'lighter' inside render().
  function renderParticleLayer(particleDt: number): void {
    if (!particleImage || !particleBuf) return;
    // Fast clear — Uint32 fill is one memset under the hood.
    particleBuf.fill(0);
    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    const sx = cssW / WORLD_W;
    const sy = cssH / WORLD_H;
    const s = Math.min(sx, sy);
    const ox = (cssW - WORLD_W * s) / 2;
    const oy = (cssH - WORLD_H * s) / 2;
    const dpr = particleDpr;
    const scaleDev = s * dpr;
    const oxDev = (ox + game.shake.x * s) * dpr;
    const oyDev = (oy + game.shake.y * s) * dpr;
    // Fused physics+plot — single iteration through the live pool.
    game.ps.stepAndRender(
      particleDt, WORLD_H,
      particleBuf, offCanvas.width, offCanvas.height,
      scaleDev, oxDev, oyDev,
    );
    offCtx!.putImageData(particleImage, 0, 0);
  }

  // ---- Loop ----
  const renderCtx = ctx;
  let rafId = 0;
  let lastNow = performance.now();
  let accumulator = 0;
  function frame(now: number): void {
    let dt = (now - lastNow) / 1000;
    lastNow = now;
    if (dt > MAX_FRAME_TIME) dt = MAX_FRAME_TIME;
    const running = game.phase !== 'paused';
    if (running) accumulator += dt;
    while (accumulator >= STEP_SEC) {
      step(STEP_SEC);
      accumulator -= STEP_SEC;
    }
    // Particles step at display rate (purely visual), not at the physics
    // substep rate. This is the single iteration over the live pool per
    // frame; combined with the fused step+plot path it roughly halves
    // particle CPU cost vs. the old two-pass setup.
    renderParticleLayer(running ? dt : 0);
    render(renderCtx, game, offCanvas, running ? dt : 0);
    syncHud();
    rafId = requestAnimationFrame(frame);
  }
  rafId = requestAnimationFrame(frame);

  function step(dt: number): void {
    if (game.phase === 'paused' || game.phase === 'upgrade_choice' || game.phase === 'game_over') return;
    game.elapsedSec += dt;

    // Auto-launch when countdown elapses
    if (game.phase === 'serving' && game.serveCountdownUntilSec !== null
        && game.elapsedSec >= game.serveCountdownUntilSec) {
      game.serveCountdownUntilSec = null;
      launchServingBall(game);
      game.phase = 'playing';
    }

    // Keyboard target nudge
    if (leftHeld) game.paddle.targetX -= 320 * dt;
    if (rightHeld) game.paddle.targetX += 320 * dt;
    game.paddle.targetX = clamp(game.paddle.targetX, game.paddle.width / 2, WORLD_W - game.paddle.width / 2);

    // Smooth paddle
    const followSpeed = 24;
    const t = 1 - Math.exp(-followSpeed * dt);
    game.paddle.prevCx = game.paddle.cx;
    game.paddle.cx = game.paddle.cx + (game.paddle.targetX - game.paddle.cx) * t;
    game.paddle.cx = clamp(game.paddle.cx, game.paddle.width / 2, WORLD_W - game.paddle.width / 2);
    game.paddle.vx = (game.paddle.cx - game.paddle.prevCx) / dt;
    game.paddle.glow = Math.max(0, game.paddle.glow - dt * 1.6);
    // Paddle motion aura — emits backwards drift particles when paddle moves
    // fast. At high momentum/combo, paddle hisses with a constant trail.
    {
      const absVx = Math.abs(game.paddle.vx);
      if (absVx > 60) {
        const k = chaosScale(game);
        const n = Math.min(10, Math.round((absVx / 200) * k * 1.5));
        const px = game.paddle.cx - Math.sign(game.paddle.vx) * (game.paddle.width / 2);
        for (let i = 0; i < n; i++) {
          game.ps.emit(
            4, // TYPE_TRAIL
            px + (Math.random() - 0.5) * game.paddle.width * 0.4,
            PADDLE_Y + Math.random() * PADDLE_H,
            -Math.sign(game.paddle.vx) * (40 + Math.random() * 100),
            -20 - Math.random() * 60,
            0.25 + Math.random() * 0.2,
            COLOR_TEAL, 2,
          );
        }
      }
    }

    // Power-up width — derive from baseWidth (which the wider_base upgrade
    // grows) and stack short-term boosts on top.
    const wantWide = game.elapsedSec < game.wideUntilSec;
    const overdrive = isOverdrive(game);
    const baseW = game.paddle.baseWidth;
    const targetW = baseW
      * (wantWide ? 1.55 : 1)
      * (overdrive ? 1.20 : 1);
    game.paddle.width = game.paddle.width + (targetW - game.paddle.width) * (1 - Math.exp(-8 * dt));

    // Combo timer — frozen during Overdrive; time_thread slows decay.
    if (game.combo > 0 && !overdrive) {
      const decayMul = game.flags.timeThread ? 0.7 : 1;
      game.comboTimerMs -= dt * 1000 * decayMul;
      if (game.comboTimerMs <= 0) game.combo = 0;
    }

    // Balls
    for (const ball of game.balls) {
      if (!ball.alive) continue;
      const speed = length(ball.v);
      const desired = targetSpeed(game.elapsedSec, game.wave);
      if (speed > 0 && Math.abs(desired - speed) > 4) {
        // Convergence rate: faster when below target so slow-impulse ramps back quickly.
        const rate = speed < desired ? 1.4 : 0.5;
        const adj = speed + (desired - speed) * rate * dt;
        scale2(ball.v, adj / Math.max(1, speed));
      }
      const dist = length(ball.v) * dt;
      const maxStep = BALL_R * 0.7;
      const steps = Math.max(1, Math.ceil(dist / maxStep));
      const subDt = dt / steps;
      for (let i = 0; i < steps; i++) {
        ball.p.x += ball.v.x * subDt;
        ball.p.y += ball.v.y * subDt;
        resolveBallCollisions(ball, game);
        if (!ball.alive) break;
      }
      ball.trail.push({ x: ball.p.x, y: ball.p.y, t: 0 });
      while (ball.trail.length > 10) ball.trail.shift();
      for (const tr of ball.trail) tr.t += dt;
      // Ball wake — sparks shed opposite to motion. Density scales with chaos
      // and ball speed so fast balls in late game leave a hot trail.
      const sp = length(ball.v);
      if (sp > 200) {
        const k = chaosScale(game);
        const wakeCount = Math.min(8, Math.round((sp / 320) * k));
        for (let i = 0; i < wakeCount; i++) {
          const back = -1 / sp;
          const jitter = 0.6;
          game.ps.emit(
            4, // TYPE_TRAIL
            ball.p.x + (Math.random() - 0.5) * 4,
            ball.p.y + (Math.random() - 0.5) * 4,
            ball.v.x * back * (40 + Math.random() * 80) + (Math.random() - 0.5) * 60 * jitter,
            ball.v.y * back * (40 + Math.random() * 80) + (Math.random() - 0.5) * 60 * jitter,
            0.18 + Math.random() * 0.22,
            game.elapsedSec < game.fireballUntilSec ? COLOR_AMBER : COLOR_TEAL,
            2,
          );
        }
      }
    }
    game.balls = game.balls.filter((b) => b.alive);
    if (game.balls.length === 0) onAllBallsLost(game);

    // Pickups
    const magnetActive = game.elapsedSec < game.magnetUntilSec;
    const magnetSpeed = magnetActive ? 220 : (game.flags.magnetHands ? 60 : 0);
    for (const pu of game.pickups) {
      if (!pu.alive) continue;
      pu.p.y += pu.v.y * dt;
      if (magnetSpeed > 0) {
        const dx = game.paddle.cx - pu.p.x;
        pu.p.x += Math.sign(dx) * Math.min(Math.abs(dx), magnetSpeed * dt);
      }
      pu.age += dt;
      // Continuous shimmer trail
      if (Math.random() < 0.7) {
        game.ps.spawnShimmer(
          pu.p.x + (Math.random() - 0.5) * 14,
          pu.p.y + (Math.random() - 0.5) * 6,
          colorU32(pickupColor(pu.kind)),
        );
      }
      if (pu.p.y > WORLD_H + 12) pu.alive = false;
      const px = game.paddle.cx;
      const py = PADDLE_Y;
      const halfW = game.paddle.width / 2 + 8;
      if (pu.p.y >= py - 6 && pu.p.y <= py + PADDLE_H + 6 &&
          pu.p.x >= px - halfW && pu.p.x <= px + halfW) {
        applyPickup(game, pu.kind);
        spawnPickupBurst(game, pu.p.x, pu.p.y, pickupColor(pu.kind));
        pushText(game, pu.p.x, pu.p.y - 8, pickupLabel(pu.kind), pickupColor(pu.kind));
        pu.alive = false;
        game.paddle.glow = 1;
        game.score += 25;
        notifyMission(game, 'pickup');
        markExciting(game, 'pickup_collected');
        game.audio.play('pickup');
        game.audio.vibe([8, 18, 14]);
      }
    }
    game.pickups = game.pickups.filter((p) => p.alive);

    // Particles step at display rate inside renderParticleLayer — not here.

    // Floating text
    for (const t of game.texts) {
      t.life -= dt;
      t.p.y += t.vy * dt;
      t.vy += 60 * dt;
    }
    game.texts = game.texts.filter((t) => t.life > 0);

    // Brick flash decay
    for (const b of game.bricks) if (!b.destroyed && b.hitFlash > 0) b.hitFlash = Math.max(0, b.hitFlash - dt * 4);

    // Ambient chaos sparkle — density scales hard with wave/combo so late
    // game has a constant background storm. Cheap shimmer particles.
    const k = chaosScale(game);
    const sparkleRate = Math.min(180, 6 + game.wave * 6 * k + game.combo * 0.8);
    let sparkleAcc = sparkleRate * dt;
    while (sparkleAcc > 0) {
      if (sparkleAcc < 1 && Math.random() > sparkleAcc) break;
      sparkleAcc -= 1;
      const palette = AMBIENT_PALETTE;
      const c = palette[(Math.random() * palette.length) | 0]!;
      game.ps.emit(
        TYPE_SHIMMER,
        Math.random() * WORLD_W,
        HUD_H + Math.random() * (PADDLE_Y - HUD_H - 20),
        (Math.random() - 0.5) * 30,
        -10 - Math.random() * 30,
        0.5 + Math.random() * 0.6,
        c, 1,
      );
    }

    // Shockwaves
    for (const sw of game.shockwaves) {
      sw.life -= dt;
      sw.r = sw.maxR * (1 - sw.life / sw.maxLife);
    }
    game.shockwaves = game.shockwaves.filter((sw) => sw.life > 0);

    // Grid wells decay
    for (const w of game.wells) w.life -= dt;
    game.wells = game.wells.filter((w) => w.life > 0);

    // Shake decay
    if (game.shake.life > 0) {
      game.shake.life -= dt;
      const m = game.shake.mag * Math.max(0, game.shake.life / 0.35);
      game.shake.x = (Math.random() - 0.5) * m;
      game.shake.y = (Math.random() - 0.5) * m;
    } else {
      game.shake.x = 0; game.shake.y = 0;
    }
    game.flashAlpha = Math.max(0, game.flashAlpha - dt * 3);

    // Momentum passive
    game.momentum += dt * PASSIVE_MOMENTUM_PER_SEC;
    game.maxComboSeen = Math.max(game.maxComboSeen, game.combo);

    // Director: dry-spell injection, mission lifecycle.
    stepDirector(game);
    stepMission(game);
    stepAffix(game, dt);

    // First-upgrade timer: offer earlier when no wave has fired the gate yet.
    if (!game.firstUpgradeOffered && game.elapsedSec >= FIRST_UPGRADE_SEC
        && game.phase === 'playing') {
      game.firstUpgradeOffered = true;
      game.lastUpgradeAtWave = game.wave;
      game.lastUpgradeAtSec = game.elapsedSec;
      offerUpgrades();
    }

    // Session arc completion — non-blocking banner once 4 minutes survived.
    if (!game.sessionComplete && game.elapsedSec >= SESSION_TARGET_SEC) {
      game.sessionComplete = true;
      game.sessionCompletedAtSec = game.elapsedSec;
      pushArcadeText(game, WORLD_W / 2, WORLD_H * 0.42, 'WAVE RIDDEN', '#fbbf24', {
        kind: 'session', priority: 100, scale: 2.0, life: 2.2,
      });
      pushArcadeText(game, WORLD_W / 2, WORLD_H * 0.42 + 26, 'You rode the wave for 4:00', '#5eead4', {
        kind: 'session', priority: 90, scale: 1.0, life: 2.2,
      });
      game.flashAlpha = Math.max(game.flashAlpha, 0.4);
      void persistSessionComplete();
    }

    // Wave clear
    const remaining = game.bricks.filter((b) => !b.destroyed).length;
    if (remaining === 0 && game.phase === 'playing') {
      onWaveCleared(game);
      markExciting(game, 'wave_cleared');
      const wavesSinceUpgrade = game.wave - 1 - game.lastUpgradeAtWave;
      const secsSinceUpgrade = game.elapsedSec - game.lastUpgradeAtSec;
      if (wavesSinceUpgrade >= UPGRADE_MIN_WAVES && secsSinceUpgrade >= UPGRADE_MIN_GAP_SEC) {
        // Mark gate before showing so a single click can't re-arm.
        game.lastUpgradeAtWave = game.wave - 1;
        game.lastUpgradeAtSec = game.elapsedSec;
        game.firstUpgradeOffered = true;
        offerUpgrades();
      }
    }

    // Momentum -> Overdrive trigger
    if (game.momentum >= MOMENTUM_TARGET) {
      startOverdrive(game);
      void persistMilestone();
    }
  }

  function syncHud(): void {
    setText(scoreEl, '[data-role="score"]', game.score.toLocaleString());
    setText(waveEl, '[data-role="wave"]', String(game.wave));
    setText(comboEl, '[data-role="combo"]', String(game.combo));
    setText(shieldEl, '[data-role="shields"]', String(Math.max(0, game.shields)));
    comboEl.classList.toggle('hot', game.combo >= 8);
    const fill = stage.querySelector('[data-role="momentum"]') as HTMLElement | null;
    if (fill) {
      const od = isOverdrive(game);
      const pct = od
        ? Math.max(0, (game.overdriveUntilSec - game.elapsedSec) / OVERDRIVE_DURATION_SEC) * 100
        : Math.min(100, game.momentum * 100);
      fill.style.width = `${pct.toFixed(1)}%`;
      fill.style.background = od
        ? 'linear-gradient(90deg, #fbbf24, #f87171)'
        : '';
    }
  }

  function hideOverlay(): void {
    overlay.classList.remove('visible');
    clear(overlay);
  }

  function showInfoOverlay(title: string, subtitle: string, body: HTMLElement): void {
    clear(overlay);
    overlay.classList.add('visible');
    const card = el('div', { class: 'ib-menu-panel' });
    const back = el('button', {
      class: 'ib-menu-back',
      attrs: { type: 'button', 'aria-label': 'Back' },
      onclick: () => { hideOverlay(); },
    }, '←');
    card.appendChild(back);
    card.appendChild(el('div', { class: 'ib-menu-title' }, title));
    card.appendChild(el('div', { class: 'ib-menu-subtitle' }, subtitle));
    const scroll = el('div', { class: 'ib-menu-scroll' });
    scroll.appendChild(body);
    card.appendChild(scroll);
    overlay.appendChild(card);
  }

  function brickSwatch(type: BrickType): HTMLElement {
    const sw = el('div', { class: `ib-brick-sw ib-brick-${type}` });
    return sw;
  }

  function pickupSwatch(kind: PickupKind): HTMLElement {
    const sw = el('div', { class: 'ib-pickup-sw' });
    sw.style.background = pickupColor(kind);
    sw.style.color = '#06161a';
    sw.textContent = pickupLabel(kind);
    return sw;
  }

  function infoRow(swatch: HTMLElement, name: string, desc: string): HTMLElement {
    return el('div', { class: 'ib-info-row' },
      swatch,
      el('div', { class: 'ib-info-text' },
        el('div', { class: 'ib-info-name' }, name),
        el('div', { class: 'ib-info-desc' }, desc),
      ),
    );
  }

  function showInstructionsOverlay(): void {
    const body = el('div', { class: 'ib-instructions' });
    body.appendChild(el('div', { class: 'ib-menu-section' }, 'HOW TO PLAY'));
    body.appendChild(el('p', {},
      'Drag, tap, or use ← → / A D to steer the paddle. Tap or press Space to launch.'));
    body.appendChild(el('p', {},
      'Break every brick to clear the wave. Chain hits without missing to grow your combo and score.'));
    body.appendChild(el('p', {},
      'Catch pickups to power up. Fill the momentum bar to trigger Overdrive — brief storms of speed and score.'));
    body.appendChild(el('p', {},
      'Survive long enough to bank the rescue session (4:00). Lose every shield and the run ends.'));

    body.appendChild(el('div', { class: 'ib-menu-section' }, 'BRICKS'));
    const bricks: Array<[BrickType, string, string]> = [
      ['normal', 'Normal', 'One hit. Standard score.'],
      ['reinforced', 'Reinforced', 'Two hits. Tougher armor.'],
      ['glass', 'Glass', 'One hit. Shatters into bonus score and shimmer.'],
      ['bomb', 'Bomb', 'Detonates on hit, clearing nearby bricks.'],
      ['bounty', 'Bounty', 'Gold brick. 5× score on break.'],
      ['crystal', 'Crystal', 'One hit. Drops a guaranteed pickup.'],
      ['mirror', 'Mirror', 'Two hits. Reflects nearby ball paths.'],
      ['boss', 'Boss', 'Massive HP. Anchors boss waves.'],
    ];
    for (const [type, name, desc] of bricks) {
      body.appendChild(infoRow(brickSwatch(type), name, desc));
    }

    body.appendChild(el('div', { class: 'ib-menu-section' }, 'PICKUPS'));
    const pickups: Array<[PickupKind, string, string]> = [
      ['wide', 'Wide Paddle', 'Paddle grows for 14s.'],
      ['multi', 'Multiball', 'Spawn extra balls.'],
      ['shield', 'Shield', 'Restore one shield.'],
      ['slow', 'Slow', 'Drops ball speed briefly.'],
      ['score2x', 'Score ×2', 'Double score for a window.'],
      ['momentum', 'Momentum', 'Big boost to the momentum bar.'],
      ['fireball', 'Fireball', 'Ball burns through bricks.'],
      ['bomb_seed', 'Bomb Seed', 'Next break drops a bomb.'],
      ['laser', 'Laser', 'Paddle fires bolts upward.'],
      ['magnet', 'Magnet', 'Pulls pickups toward paddle.'],
      ['safe', 'Safe Ball', 'One free miss saved.'],
      ['prism_split', 'Prism Split', 'Balls split on next brick hit.'],
      ['chain_lightning', 'Chain Lightning', 'Hits arc to nearby bricks.'],
    ];
    for (const [kind, name, desc] of pickups) {
      body.appendChild(infoRow(pickupSwatch(kind), name, desc));
    }

    body.appendChild(el('div', { class: 'ib-menu-section' }, 'CONTROLS'));
    body.appendChild(el('p', { class: 'ib-instructions-keys' },
      '← → / A D move · Space or tap launch · P / Esc pause'));

    showInfoOverlay('INSTRUCTIONS', 'How the storm works', body);
  }

  function showAchievementsOverlay(): void {
    const have = new Set(persisted.achievements);
    const list = el('div', { class: 'ib-achievement-list' });
    for (const a of ACHIEVEMENTS) {
      const unlocked = have.has(a.id);
      const row = el('div', { class: unlocked ? 'ib-ach-row unlocked' : 'ib-ach-row locked' },
        el('span', { class: 'ib-ach-mark' }, unlocked ? '★' : '·'),
        el('div', { class: 'ib-ach-text' },
          el('div', { class: 'ib-ach-name' }, a.name),
          el('div', { class: 'ib-ach-desc' }, a.desc),
        ),
      );
      list.appendChild(row);
    }
    showInfoOverlay(
      'ACHIEVEMENTS',
      `${persisted.achievements.length} / ${ACHIEVEMENTS.length} unlocked`,
      list,
    );
  }

  function showLeaderboardOverlay(): void {
    const body = el('div', {});
    if (persisted.highScores.length === 0) {
      body.appendChild(el('div', { class: 'ib-overlay-empty' }, 'No runs yet. Be the first spark.'));
    } else {
      const table = el('div', { class: 'ib-overlay-highscores' });
      persisted.highScores.forEach((r, i) => {
        const row = el('div', { class: 'ib-hs-row' },
          el('span', { class: 'ib-hs-rank' }, `#${i + 1}`),
          el('span', { class: 'ib-hs-score' }, r.score.toLocaleString()),
          el('span', { class: 'ib-hs-date' }, formatScoreDate(r.endedAtISO)),
          el('span', { class: 'ib-hs-wave' }, `W${r.wave}`),
          el('span', { class: 'ib-hs-combo' }, `${r.maxCombo}x`),
        );
        table.appendChild(row);
      });
      body.appendChild(table);
    }
    showInfoOverlay(
      'HALL OF SPARKS',
      persisted.highScores.length > 0 ? 'Top runs across all sessions' : '',
      body,
    );
  }

  function togglePause(): void {
    if (game.phase === 'paused') {
      resume();
      return;
    }
    if (game.phase === 'playing' || game.phase === 'serving') {
      game.phase = 'paused';
      showPauseCanvasMenu();
    }
  }
  function resume(): void {
    if (game.phase !== 'paused') return;
    game.phase = game.balls.some((b) => attached(b)) ? 'serving' : 'playing';
    hideCanvasMenu();
    hideOverlay();
  }

  async function persistMilestone(): Promise<void> {
    persisted.momentumMilestones += 1;
    persisted.bestMomentumMilestones = Math.max(persisted.bestMomentumMilestones, game.momentumMilestones);
    persisted.bestScore = Math.max(persisted.bestScore, game.score);
    persisted.bestCombo = Math.max(persisted.bestCombo, game.maxComboSeen);
    persisted.bestWave = Math.max(persisted.bestWave, game.wave);
    await minigameState.put(MODULE_ID, persisted);
  }

  async function persistSessionComplete(): Promise<void> {
    persisted.sessionsCompleted += 1;
    // Note: totalRescueSeconds is bumped once in persistRunEnd from the full
    // elapsed time so a session arc + later game-over don't double-count.
    await minigameState.put(MODULE_ID, persisted);
  }

  async function persistRunEnd(): Promise<void> {
    if (game.runRecorded) return;
    if (!game.runStarted) return;
    game.runRecorded = true;
    persisted.runs += 1;
    persisted.bestScore = Math.max(persisted.bestScore, game.score);
    persisted.bestCombo = Math.max(persisted.bestCombo, game.maxComboSeen);
    persisted.bestWave = Math.max(persisted.bestWave, game.wave);

    const entry = buildHighScoreEntry({
      score: game.score,
      wave: game.wave,
      maxCombo: game.maxComboSeen,
      durationSec: game.elapsedSec,
      momentumMilestones: game.momentumMilestones,
    });
    game.lastRunRank = insertHighScore(persisted, entry);

    // Bump career-cumulative counters before evaluating achievements so
    // career-tier checks see the updated totals.
    persisted.totalBricksBroken += game.bricksBroken;
    persisted.totalBountyBricks += game.bountyBricksBroken;
    persisted.totalPerfectHits += game.perfectHits;
    persisted.totalMissionsCompleted += game.missionsCompleted;
    persisted.totalShieldSaves += game.shieldSaves;
    persisted.totalOverdrives += game.overdriveCount;
    persisted.totalBossesDefeated += game.bossesDefeated;
    persisted.totalRescueSeconds += Math.round(game.elapsedSec);

    // XP grant — reward staying with it, clearing waves, and completing missions.
    let xp = 0;
    xp += Math.max(0, game.wave - 1) * 25;
    xp += game.missionsCompleted * 40;
    xp += game.overdriveCount * 60;
    if (game.elapsedSec >= 180) xp += 200;
    if (game.sessionComplete) xp += 100;
    if (game.lastRunRank !== null) xp += 100;
    if (game.shieldSaves > 0) xp += 30;
    game.xpEarned = xp;
    game.levelsGained = addXp(persisted, xp);

    const unlocked = evaluateAchievements(persisted, {
      score: game.score,
      wave: game.wave,
      maxCombo: game.maxComboSeen,
      durationSec: game.elapsedSec,
      shieldSaves: game.shieldSaves,
      perfectHits: game.perfectHits,
      missionsCompleted: game.missionsCompleted,
      bountyBricksBroken: game.bountyBricksBroken,
      bricksBroken: game.bricksBroken,
      bossesDefeated: game.bossesDefeated,
      rank: game.lastRunRank,
      overdrivesTriggered: game.overdriveCount,
      sessionCompleted: game.sessionComplete,
    });
    game.newlyUnlocked = unlocked;

    await minigameState.put(MODULE_ID, persisted);
  }

  function quit(): void {
    cleanup();
    // Rescue session counts as completed if they hit the session arc target
    // or banked at least one Overdrive.
    const completed = game.sessionComplete || game.momentumMilestones > 0;
    if (game.phase !== 'game_over' && game.runStarted) void persistRunEnd();
    resolve({
      kind: completed ? 'completed' : 'abandoned',
      details: {
        score: game.score, wave: game.wave,
        maxCombo: game.maxComboSeen,
        momentumMilestones: game.momentumMilestones,
        sessionCompleted: game.sessionComplete,
      },
    });
  }

  function cleanup(): void {
    cancelAnimationFrame(rafId);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    ro.disconnect();
    document.body.classList.remove('ib-active');
  }

  function onAllBallsLost(game: Game): void {
    game.shields -= 1;
    if (game.comboInsurance) {
      game.comboInsurance = false;
      pushArcadeText(game, game.paddle.cx, PADDLE_Y - 20, 'CHAIN VAULTED!', '#5eead4', {
        kind: 'save', priority: 80, scale: 1.1, life: 1.0,
      });
    } else {
      game.combo = 0;
    }
    if (game.shields < 0) {
      game.phase = 'game_over';
      void persistRunEnd().then(showGameOverOverlay);
      return;
    }
    // Shield save — recovery blast staged as a comeback moment.
    game.shieldSaves += 1;
    game.lastShieldLossAtSec = game.elapsedSec;
    shieldRecoveryBlast(game);
    markExciting(game, 'save_triggered');
    spawnServeBall(game);
    game.phase = 'serving';
    game.serveCountdownUntilSec = game.elapsedSec + 2.6;
  }

  function showGameOverOverlay(): void {
    if (game.lastRunRank !== null) game.audio.play('high_score');
    else if (game.newlyUnlocked.length > 0) game.audio.play('achievement');
    clear(overlay);
    overlay.classList.add('visible');
    const card = el('div', { class: 'ib-overlay-card' });
    card.appendChild(el('div', { class: 'ib-overlay-title' }, 'RUN COMPLETE'));
    const stats = el('div', { class: 'ib-overlay-stats' },
      el('div', {}, `Score ${game.score.toLocaleString()}`),
      el('div', {}, `Wave ${game.wave}  ·  Chain ${game.maxComboSeen}x`),
      el('div', {}, `You stayed with it for ${formatDuration(game.elapsedSec)}.`),
    );
    card.appendChild(stats);
    if (game.xpEarned > 0) {
      const lvl = persisted.progression.level;
      const cur = persisted.progression.xp;
      const need = xpForLevel(lvl);
      const pct = Math.min(100, Math.round((cur / need) * 100));
      const xpLine = el('div', { class: 'ib-overlay-xp' },
        el('span', { class: 'ib-xp-amount' }, `XP +${game.xpEarned}`),
        el('span', { class: 'ib-xp-level' }, `Lv ${lvl} · ${cur}/${need}`),
      );
      card.appendChild(xpLine);
      const bar = el('div', { class: 'ib-xp-bar' },
        el('div', { class: 'ib-xp-fill', attrs: { style: `width:${pct}%` } }),
      );
      card.appendChild(bar);
      if (game.levelsGained > 0) {
        card.appendChild(el('div', { class: 'ib-overlay-flair' },
          `LEVEL UP +${game.levelsGained}!  →  Lv ${lvl}`));
      }
    }
    if (game.lastRunRank !== null) {
      card.appendChild(el('div', { class: 'ib-overlay-flair' }, `NEW LEGEND! #${game.lastRunRank} ALL-TIME`));
    } else if (persisted.highScores.length >= 10) {
      const lowest = persisted.highScores[persisted.highScores.length - 1]!;
      const gap = Math.max(0, lowest.score - game.score + 1);
      card.appendChild(el('div', { class: 'ib-overlay-flair muted' }, `Good run — ${gap.toLocaleString()} to top 10`));
    }
    if (game.newlyUnlocked.length > 0) {
      const ach = el('div', { class: 'ib-overlay-achievements' });
      ach.appendChild(el('div', { class: 'ib-overlay-section' }, 'ACHIEVEMENTS UNLOCKED'));
      for (const a of game.newlyUnlocked) {
        ach.appendChild(el('div', {}, `★ ${a.name} — ${a.desc}`));
      }
      card.appendChild(ach);
    }
    if (persisted.highScores.length > 0) {
      const table = el('div', { class: 'ib-overlay-highscores' });
      table.appendChild(el('div', { class: 'ib-overlay-section' }, 'HALL OF SPARKS'));
      const rows = persisted.highScores.slice(0, 5);
      rows.forEach((r, i) => {
        const row = el('div', { class: i + 1 === game.lastRunRank ? 'ib-hs-row new' : 'ib-hs-row' },
          el('span', { class: 'ib-hs-rank' }, `#${i + 1}`),
          el('span', { class: 'ib-hs-score' }, r.score.toLocaleString()),
          el('span', { class: 'ib-hs-date' }, formatScoreDate(r.endedAtISO)),
          el('span', { class: 'ib-hs-wave' }, `W${r.wave}`),
          el('span', { class: 'ib-hs-combo' }, `${r.maxCombo}x`),
        );
        table.appendChild(row);
      });
      card.appendChild(table);
    }
    const actions = el('div', { class: 'ib-overlay-actions' });
    actions.appendChild(el('button', {
      class: 'btn primary',
      onclick: () => quit(),
    }, game.sessionComplete ? 'Exit with win' : 'Done'));
    card.appendChild(actions);
    overlay.appendChild(card);
  }

  function offerUpgrades(): void {
    const choices = pickUpgradeChoices(game, 3);
    if (choices.length === 0) {
      // Nothing meaningful left to offer; skip pause.
      return;
    }
    game.phase = 'upgrade_choice';
    markExciting(game, 'upgrade_offered');
    clear(overlay);
    overlay.classList.add('visible');
    const card = el('div', { class: 'ib-overlay-card ib-upgrade-card' });
    card.appendChild(el('div', { class: 'ib-upgrade-title' }, 'CHOOSE YOUR SPARK'));
    card.appendChild(el('div', { class: 'ib-upgrade-sub' }, 'The storm bends around your next move.'));
    const grid = el('div', { class: 'ib-upgrade-grid' });
    for (const def of choices) {
      const tile = el('button', {
        class: 'ib-upgrade-tile',
        attrs: { type: 'button' },
        onclick: () => {
          def.apply(game);
          game.upgrades.add(def.id);
          pushArcadeText(game, WORLD_W / 2, BRICK_TOP + 60, def.name.toUpperCase(), '#fbbf24', {
            kind: 'achievement', priority: 90, scale: 1.6, life: 1.4,
          });
          game.flashAlpha = 0.35;
          hideOverlay();
          game.phase = game.balls.some((b) => attached(b)) ? 'serving' : 'playing';
        },
      },
        el('div', { class: 'ib-upgrade-name' }, def.name),
        el('div', { class: 'ib-upgrade-desc' }, def.desc),
      );
      grid.appendChild(tile);
    }
    card.appendChild(grid);
    overlay.appendChild(card);
  }
}

// ---- Helpers / pure-ish functions ----

function attached(ball: Ball): boolean {
  return ball.v.x === 0 && ball.v.y === 0;
}

function setText(root: HTMLElement, sel: string, val: string): void {
  const node = root.querySelector(sel);
  if (node) node.textContent = val;
}

/**
 * Chaos multiplier — drives particle density, sparkle rate, and burst sizes.
 * Ramps with wave (progression), combo (skill), and momentum (build-up) so
 * late-game/streak moments feel visibly more chaotic than wave 1.
 */
/**
 * After a wall bounce, force the ball's velocity to keep at least some
 * vertical component. Without this, a paddle hit at a steep angle can drop the
 * ball into a wall-to-wall horizontal loop the player can't influence. We
 * preserve total speed and X direction; only the angle is corrected. If vy is
 * essentially zero, bias downward so the ball heads toward the paddle.
 */
function enforceMinVerticalAngle(ball: Ball): void {
  const speed = length(ball.v);
  if (speed < 1) return;
  const minRatio = 0.38; // ~22.3° from horizontal
  if (Math.abs(ball.v.y) / speed >= minRatio) return;
  const dir = ball.v.y < -0.01 ? -1 : 1; // default downward toward paddle
  const minVy = speed * minRatio;
  ball.v.y = dir * minVy;
  const newVxMag = Math.sqrt(Math.max(0, speed * speed - ball.v.y * ball.v.y));
  const vxSign = ball.v.x >= 0 ? 1 : -1;
  ball.v.x = vxSign * newVxMag;
}

function chaosScale(game: Game): number {
  const wavePart = (game.wave - 1) * 0.28;
  const comboPart = Math.min(2.5, game.combo * 0.045);
  const momentumPart = game.momentum * 1.2;
  return Math.min(6, 1 + wavePart + comboPart + momentumPart);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function length(v: Vec): number { return Math.sqrt(v.x * v.x + v.y * v.y); }
function scale2(v: Vec, k: number): void { v.x *= k; v.y *= k; }

function targetSpeed(elapsedSec: number, wave: number): number {
  const timeBoost = Math.min(1.4, 1 + elapsedSec / 360);
  const waveBoost = 1 + wave * 0.045;
  return clamp(INITIAL_BALL_SPEED * timeBoost * waveBoost, INITIAL_BALL_SPEED, MAX_BALL_SPEED);
}

function buildWave(game: Game): void {
  game.bricks = [];
  const wave = game.wave;
  game.bossActive = false;
  game.movingRowOffset = 0;
  game.movingRowIndex = -1;
  game.affixTick = 0;

  // Pick wave affix (skip wave 1, skip boss waves).
  const isBossWave = wave > 1 && wave % 5 === 0;
  if (isBossWave) {
    game.waveAffix = 'none';
    buildBossWave(game);
    return;
  }
  game.waveAffix = pickWaveAffix(wave);

  const glassBoost = game.waveAffix === 'glass_garden' ? 0.18 : 0;
  const filledRows = Math.min(ROWS, 4 + Math.floor(wave / 2));
  for (let r = 0; r < filledRows; r++) {
    for (let c = 0; c < COLS; c++) {
      const isCenterLane = wave % 3 === 0 && (c === Math.floor(COLS / 2) - 1 || c === Math.floor(COLS / 2));
      if (isCenterLane && r >= filledRows - 2) continue;
      if (wave === 1 && r === 0 && (c % 2 === 0)) continue;

      const x = BRICK_GAP + c * (BRICK_W + BRICK_GAP);
      const y = BRICK_TOP + r * (BRICK_H + BRICK_GAP);
      const reinforcedChance = Math.min(0.45, 0.10 + wave * 0.030);
      const reinforced3Chance = Math.min(0.20, Math.max(0, (wave - 4) * 0.030));
      const glassChance = (wave >= 2 ? 0.06 : 0.02) + glassBoost;
      const bombChance = wave >= 2 ? Math.min(0.08, 0.02 + wave * 0.006) : 0;
      const crystalChance = wave >= 4 ? 0.05 : 0;
      const mirrorChance = wave >= 6 ? 0.04 : 0;
      const roll = Math.random();
      let type: BrickType = 'normal';
      let hp = 1;
      let cursor = 0;
      cursor += reinforced3Chance;
      if (roll < cursor && r < Math.max(2, filledRows - 2)) { type = 'reinforced'; hp = 3; }
      else {
        cursor = reinforcedChance;
        if (roll < cursor && r < Math.max(2, filledRows - 1)) { type = 'reinforced'; hp = 2; }
        else {
          cursor += bombChance;
          if (roll < cursor) { type = 'bomb'; hp = 1; }
          else {
            cursor += glassChance;
            if (roll < cursor) { type = 'glass'; hp = 1; }
            else {
              cursor += crystalChance;
              if (roll < cursor) { type = 'crystal'; hp = 1; }
              else {
                cursor += mirrorChance;
                if (roll < cursor) { type = 'mirror'; hp = 2; }
              }
            }
          }
        }
      }

      const hue =
        type === 'reinforced' ? 285 + r * 4
        : type === 'glass' ? 195 + c * 3
        : type === 'bomb' ? 12 + (c % 4) * 6
        : type === 'crystal' ? 210 + c * 4
        : type === 'mirror' ? 0
        : 165 + r * 8 + c * 2;
      game.bricks.push({
        col: c, row: r, x, y, w: BRICK_W, h: BRICK_H,
        type, hp, maxHp: hp,
        destroyed: false, hitFlash: 0,
        hue,
      });
    }
  }
  // Bounty Hunter upgrade: mark one random normal brick as gold-tier per wave.
  if (game.flags.bountyBricks) {
    const eligible = game.bricks.filter((b) => b.type === 'normal');
    const pick2 = eligible[(Math.random() * eligible.length) | 0];
    if (pick2) {
      pick2.type = 'bounty';
      pick2.hp = 1;
      pick2.maxHp = 1;
      pick2.hue = 48;
    }
  }
  // Moving row affix: pick a row to drift.
  if (game.waveAffix === 'moving_row' && filledRows > 1) {
    game.movingRowIndex = 1 + ((Math.random() * Math.max(1, filledRows - 2)) | 0);
  }
}

function pickWaveAffix(wave: number): WaveAffix {
  if (wave < 3) return 'none';
  const pool: WaveAffix[] = ['comet_rain', 'glass_garden', 'bomb_bloom', 'star_rush'];
  if (wave >= 6) pool.push('moving_row');
  return pool[(Math.random() * pool.length) | 0] ?? 'none';
}

function buildBossWave(game: Game): void {
  game.bossActive = true;
  const wave = game.wave;
  // A 4-cell-wide x 2-cell-tall boss in the upper-center area.
  const bossCols = 4;
  const bossRows = 2;
  const bossX = BRICK_GAP + ((COLS - bossCols) / 2) * (BRICK_W + BRICK_GAP);
  const bossY = BRICK_TOP;
  const bossW = bossCols * BRICK_W + (bossCols - 1) * BRICK_GAP;
  const bossH = bossRows * BRICK_H + (bossRows - 1) * BRICK_GAP;
  const bossHp = 20 + Math.floor(wave / 5) * 8;
  game.bricks.push({
    col: 2, row: 0, x: bossX, y: bossY, w: bossW, h: bossH,
    type: 'boss', hp: bossHp, maxHp: bossHp,
    destroyed: false, hitFlash: 0,
    hue: 18,
  });
  // Skirt of normal/reinforced bricks below boss.
  const skirtRows = 3;
  const skirtTop = bossY + bossH + BRICK_GAP + 6;
  for (let r = 0; r < skirtRows; r++) {
    for (let c = 0; c < COLS; c++) {
      if (Math.random() < 0.15) continue; // sparse
      const x = BRICK_GAP + c * (BRICK_W + BRICK_GAP);
      const y = skirtTop + r * (BRICK_H + BRICK_GAP);
      const isReinforced = Math.random() < 0.25;
      game.bricks.push({
        col: c, row: r + 3, x, y, w: BRICK_W, h: BRICK_H,
        type: isReinforced ? 'reinforced' : 'normal',
        hp: isReinforced ? 2 : 1,
        maxHp: isReinforced ? 2 : 1,
        destroyed: false, hitFlash: 0,
        hue: isReinforced ? 285 + r * 4 : 165 + r * 8 + c * 2,
      });
    }
  }
}

function spawnServeBall(game: Game): void {
  game.balls = [{
    p: { x: game.paddle.cx, y: PADDLE_Y - BALL_R - 2 },
    v: { x: 0, y: 0 },
    alive: true,
    trail: [],
  }];
  game.phase = 'serving';
}

function launchServingBall(game: Game): void {
  game.runStarted = true;
  for (const b of game.balls) {
    if (attached(b)) {
      const angle = (Math.random() * 36 - 18) * (Math.PI / 180);
      const speed = targetSpeed(game.elapsedSec, game.wave);
      b.v.x = Math.sin(angle) * speed;
      b.v.y = -Math.cos(angle) * speed;
    }
  }
}

function applyPickup(game: Game, kind: PickupKind): void {
  const now = game.elapsedSec;
  if (kind === 'wide') {
    game.wideUntilSec = now + 14;
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Wide Paddle', '#5eead4');
  } else if (kind === 'multi') {
    splitBalls(game);
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Multiball', '#67e8f9');
  } else if (kind === 'shield') {
    game.shields = Math.min(SHIELDS_START, game.shields + 1);
    pushText(game, game.paddle.cx, PADDLE_Y - 12, '+1 Shield', '#34d399');
  } else if (kind === 'slow') {
    // Instant impulse — slow current balls hard, then convergence loop ramps back.
    for (const ball of game.balls) {
      if (!ball.alive || attached(ball)) continue;
      ball.v.x *= 0.55;
      ball.v.y *= 0.55;
    }
    game.shockwaves.push({
      p: { x: game.paddle.cx, y: PADDLE_Y - 30 }, r: 0, maxR: 140,
      life: 0.4, maxLife: 0.4, color: '#93c5fd',
    });
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Slow Burst', '#93c5fd');
  } else if (kind === 'score2x') {
    game.score2xUntilSec = now + 10;
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Score ×2', '#fbbf24');
  } else if (kind === 'momentum') {
    game.momentum = Math.min(MOMENTUM_TARGET, game.momentum + 0.10);
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Momentum +', '#a78bfa');
  } else if (kind === 'fireball') {
    game.fireballUntilSec = now + 7;
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Fireball', '#f87171');
  } else if (kind === 'bomb_seed') {
    seedBombs(game, 3);
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Bomb Seed', '#fbbf24');
  } else if (kind === 'laser') {
    laserSweepRow(game);
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Laser Sweep', '#67e8f9');
  } else if (kind === 'magnet') {
    game.magnetUntilSec = now + 8;
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Magnet', '#5eead4');
  } else if (kind === 'prism_split') {
    prismSplitBalls(game);
    game.prismSplitUntilSec = now + 4;
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Prism Split', '#a78bfa');
  } else if (kind === 'chain_lightning') {
    game.chainLightningHits += 6;
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Chain Lightning', '#67e8f9');
  } else {
    // safe
    game.safeBallReady = true;
    pushText(game, game.paddle.cx, PADDLE_Y - 12, 'Safety Ball', '#a78bfa');
  }
}

function prismSplitBalls(game: Game): void {
  const live = game.balls.filter((b) => b.alive && !attached(b));
  const seed = live[0];
  if (!seed) {
    launchServingBall(game);
    return;
  }
  const liveCount = game.balls.filter((b) => b.alive).length;
  const room = Math.max(0, MAX_BALLS - liveCount);
  if (room === 0) return;
  const toAdd = Math.min(room, 3);
  const speed = length(seed.v) || INITIAL_BALL_SPEED;
  for (let i = 0; i < toAdd; i++) {
    const angle = ((i + 1) / (toAdd + 1) - 0.5) * (Math.PI * 0.6);
    game.balls.push({
      p: { x: seed.p.x, y: seed.p.y },
      v: { x: Math.sin(angle) * speed, y: -Math.cos(angle) * speed },
      alive: true,
      trail: [],
    });
  }
}

function seedBombs(game: Game, count: number): void {
  const eligible = game.bricks.filter((b) => !b.destroyed && b.type !== 'bomb');
  for (let i = eligible.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = eligible[i]!; const b = eligible[j]!;
    eligible[i] = b; eligible[j] = a;
  }
  for (const b of eligible.slice(0, count)) {
    b.type = 'bomb';
    b.hp = 1;
    b.maxHp = 1;
    b.hue = 12 + (b.col % 4) * 6;
    b.hitFlash = 0.6;
  }
}

function laserSweepRow(game: Game): void {
  const remaining = game.bricks.filter((b) => !b.destroyed);
  if (remaining.length === 0) return;
  // Find lowest non-empty row
  let targetRow = -1;
  for (let r = 99; r >= 0; r--) {
    if (remaining.some((b) => b.row === r)) { targetRow = r; break; }
  }
  if (targetRow < 0) return;
  game.shockwaves.push({
    p: { x: WORLD_W / 2, y: BRICK_TOP + targetRow * (BRICK_H + BRICK_GAP) + BRICK_H / 2 },
    r: 0, maxR: 260, life: 0.45, maxLife: 0.45, color: '#67e8f9',
  });
  game.flashAlpha = Math.max(game.flashAlpha, 0.45);
  game.shake.mag = Math.max(game.shake.mag, 6);
  game.shake.life = Math.max(game.shake.life, 0.3);
  for (const b of remaining) {
    if (b.row !== targetRow) continue;
    const hit: BrickHit = {
      fromExplosion: true,
      impactX: b.x + b.w / 2, impactY: b.y,
      impactVx: 0, impactVy: 280,
    };
    damageBrick(b, game, hit);
    if (!b.destroyed) damageBrick(b, game, hit);
  }
}

function splitBalls(game: Game): void {
  const live = game.balls.filter((b) => b.alive && !attached(b));
  const seed = live[0];
  if (!seed) {
    launchServingBall(game);
    return;
  }
  const liveCount = game.balls.filter((b) => b.alive).length;
  const room = Math.max(0, MAX_BALLS - liveCount);
  if (room === 0) return;
  const toAdd = Math.min(2, room);
  const speed = length(seed.v) || INITIAL_BALL_SPEED;
  for (let i = 0; i < toAdd; i++) {
    const angle = (Math.random() * 60 - 30) * (Math.PI / 180);
    game.balls.push({
      p: { x: seed.p.x, y: seed.p.y },
      v: { x: Math.sin(angle) * speed, y: -Math.cos(angle) * speed },
      alive: true,
      trail: [],
    });
  }
}

function pickupColor(kind: PickupKind): string {
  switch (kind) {
    case 'wide': return '#5eead4';
    case 'multi': return '#67e8f9';
    case 'shield': return '#34d399';
    case 'slow': return '#93c5fd';
    case 'score2x': return '#fbbf24';
    case 'momentum': return '#a78bfa';
    case 'fireball': return '#f87171';
    case 'bomb_seed': return '#fbbf24';
    case 'laser': return '#67e8f9';
    case 'magnet': return '#5eead4';
    case 'safe': return '#a78bfa';
    case 'prism_split': return '#a78bfa';
    case 'chain_lightning': return '#67e8f9';
  }
}

function pickupLabel(kind: PickupKind): string {
  switch (kind) {
    case 'wide': return '↔';
    case 'multi': return '×3';
    case 'shield': return '◈';
    case 'slow': return '⤓';
    case 'score2x': return '×2';
    case 'momentum': return '★';
    case 'fireball': return '🔥';
    case 'bomb_seed': return '✦';
    case 'laser': return '⚡';
    case 'magnet': return '◉';
    case 'safe': return '♡';
    case 'prism_split': return '◇';
    case 'chain_lightning': return '⟿';
  }
}

function maybeDropPickup(game: Game, x: number, y: number): void {
  // Hard cap concurrent falling pickups; one at a time keeps the screen clean.
  if (game.pickups.filter((p) => p.alive).length >= MAX_FALLING_PICKUPS) return;
  const starRush = game.waveAffix === 'star_rush';
  const cooldown = starRush ? PICKUP_COOLDOWN_SEC * 0.55 : PICKUP_COOLDOWN_SEC;
  if (game.elapsedSec - game.lastPickupAtSec < cooldown) return;
  const droughtBoost = Math.min(0.04, game.bricksSinceDrop * 0.005);
  const comboBoost = Math.min(0.015, game.combo * 0.0005);
  const odBoost = isOverdrive(game) ? 0.05 : 0;
  const affixBoost = starRush ? 0.08 : 0;
  const chance = clamp(
    PICKUP_BASE_CHANCE + droughtBoost + comboBoost + game.pickupChanceBonus + odBoost + affixBoost,
    0.01, PICKUP_CHANCE_CAP,
  );
  if (Math.random() > chance) return;
  // Weighted pool. Comfort pickups common; high-impact ones rare.
  const kind = pickWeightedPickup(game);
  game.pickups.push({
    p: { x, y },
    v: { x: 0, y: PICKUP_FALL },
    kind,
    alive: true,
    age: 0,
  });
  game.bricksSinceDrop = 0;
  game.lastPickupAtSec = game.elapsedSec;
}

/** 0..1 — how comfortable the player looks right now. */
function computeComfort(game: Game): number {
  let c = 0;
  if (game.shields >= SHIELDS_START) c += 0.25;
  if (game.combo >= 15) c += 0.2;
  if (game.elapsedSec - game.lastShieldLossAtSec > 60) c += 0.2;
  if (isOverdrive(game)) c += 0.2;
  if (game.overdriveCount >= 2) c += 0.15;
  return clamp(c, 0, 1);
}

function pickWeightedPickup(game: Game): PickupKind {
  const comfort = computeComfort(game);
  const struggling = 1 - comfort;
  // Weighted pool: cruising players get spectacle, struggling players get safety.
  const w: Array<[PickupKind, number]> = [
    ['wide', 1.0 + struggling * 0.6],
    ['multi', 1.2],
    ['shield', 0.7 + struggling * 1.4],
    ['slow', 0.6 + struggling * 1.2],
    ['score2x', 1.0 + comfort * 0.8],
    ['momentum', 1.0],
    ['fireball', 0.8 + comfort * 0.6],
    ['bomb_seed', 0.8 + comfort * 0.6],
    ['laser', 0.7 + comfort * 0.4],
    ['magnet', 0.8 + struggling * 0.4],
    ['safe', 0.5 + struggling * 1.4],
    ['prism_split', 0.6 + comfort * 0.6],
    ['chain_lightning', 0.6 + comfort * 0.6],
  ];
  let total = 0;
  for (const [, weight] of w) total += weight;
  let roll = Math.random() * total;
  for (const [kind, weight] of w) {
    roll -= weight;
    if (roll <= 0) return kind;
  }
  return 'wide';
}

function comboMultiplier(combo: number): number {
  const capped = Math.min(combo, COMBO_MULT_CAP_COUNT);
  return 1 + Math.floor(capped / 5) * 0.12;
}

function resolveBallCollisions(ball: Ball, game: Game): void {
  // Walls — enforce minimum vertical angle on every wall bounce so the ball
  // can't get trapped in a near-horizontal wall-to-wall loop with the player
  // unable to do anything.
  if (ball.p.x - BALL_R < 0) {
    ball.p.x = BALL_R; ball.v.x = Math.abs(ball.v.x);
    enforceMinVerticalAngle(ball);
    spawnSparks(game, ball.p.x, ball.p.y, '#5eead4');
  }
  if (ball.p.x + BALL_R > WORLD_W) {
    ball.p.x = WORLD_W - BALL_R; ball.v.x = -Math.abs(ball.v.x);
    enforceMinVerticalAngle(ball);
    spawnSparks(game, ball.p.x, ball.p.y, '#5eead4');
  }
  if (ball.p.y - BALL_R < HUD_H) {
    ball.p.y = HUD_H + BALL_R; ball.v.y = Math.abs(ball.v.y);
    spawnSparks(game, ball.p.x, ball.p.y, '#5eead4');
  }

  // Paddle
  const paddle = game.paddle;
  const paddleLeft = paddle.cx - paddle.width / 2;
  const paddleRight = paddle.cx + paddle.width / 2;
  const paddleTop = PADDLE_Y;
  const paddleBottom = PADDLE_Y + PADDLE_H;
  if (ball.v.y > 0
      && ball.p.y + BALL_R >= paddleTop
      && ball.p.y - BALL_R <= paddleBottom
      && ball.p.x >= paddleLeft - 6
      && ball.p.x <= paddleRight + 6) {
    const rel = clamp((ball.p.x - paddle.cx) / (paddle.width / 2), -1, 1);
    const absRel = Math.abs(rel);
    const isCenterPerfect = absRel < PERFECT_CENTER_ABS;
    const isEdgePerfect = absRel > PERFECT_EDGE_LO && absRel < PERFECT_EDGE_HI;
    const maxAngle = (65 * Math.PI) / 180;
    const angle = rel * maxAngle;
    const baseSpeed = Math.max(length(ball.v), INITIAL_BALL_SPEED);
    const speed = isEdgePerfect ? baseSpeed * 1.08 : baseSpeed;
    ball.v.x = Math.sin(angle) * speed + paddle.vx * 0.18;
    ball.v.y = -Math.cos(angle) * speed;
    ball.p.y = paddleTop - BALL_R - 0.5;
    paddle.glow = 1;
    spawnSparks(game, ball.p.x, paddleTop, '#5eead4');
    game.wells.push({ p: { x: ball.p.x, y: paddleTop }, strength: 0.8, life: 0.35, maxLife: 0.35 });
    if (isCenterPerfect || isEdgePerfect) {
      onPerfectPaddleHit(game, ball, isEdgePerfect ? 'edge' : 'center');
    } else {
      game.audio.play('paddle');
      game.audio.vibe(6);
    }
    return;
  }

  // Bottom miss — try near-miss save (rare mercy) before the safe-ball charge.
  if (ball.p.y - BALL_R > WORLD_H) {
    if (ball.v.y > 0 && ball.p.y < WORLD_H + 28 && maybeNearMissSave(ball, game)) {
      return;
    }
    if (game.safeBallReady) {
      // One-shot rebound — reposition above bottom and reverse vy.
      game.safeBallReady = false;
      ball.p.y = WORLD_H - BALL_R - 30;
      ball.v.y = -Math.abs(ball.v.y || INITIAL_BALL_SPEED);
      game.shockwaves.push({
        p: { x: ball.p.x, y: WORLD_H - 8 }, r: 0, maxR: 160,
        life: 0.45, maxLife: 0.45, color: '#a78bfa',
      });
      game.flashAlpha = Math.max(game.flashAlpha, 0.4);
      pushText(game, ball.p.x, WORLD_H - 30, 'SAVED!', '#a78bfa', 1.4, 1.0);
      return;
    }
    ball.alive = false;
    return;
  }

  // Bricks
  const fireball = game.elapsedSec < game.fireballUntilSec;
  for (const b of game.bricks) {
    if (b.destroyed) continue;
    const closestX = clamp(ball.p.x, b.x, b.x + b.w);
    const closestY = clamp(ball.p.y, b.y, b.y + b.h);
    const dx = ball.p.x - closestX;
    const dy = ball.p.y - closestY;
    const sqDist = dx * dx + dy * dy;
    if (sqDist > BALL_R * BALL_R) continue;

    // Comet Core: every Nth eligible hit pierces a single weak brick.
    let cometPierce = false;
    if (game.cometEvery > 0 && b.hp <= 1 && b.type !== 'reinforced') {
      game.cometCounter += 1;
      if (game.cometCounter >= game.cometEvery) {
        game.cometCounter = 0;
        cometPierce = true;
      }
    }
    const willPierce = (fireball && b.hp <= 1 && b.type !== 'reinforced') || cometPierce;
    // Capture impact point + ball velocity BEFORE reflection — feeds cell fracture.
    const impactX = closestX;
    const impactY = closestY;
    const preVx = ball.v.x;
    const preVy = ball.v.y;
    if (!willPierce) {
      let nx: number;
      let ny: number;
      let pen: number;
      if (sqDist > 0.0001) {
        // Ball center outside brick (typical case) — normal points from
        // closest face out toward the ball.
        const dist = Math.sqrt(sqDist);
        nx = dx / dist;
        ny = dy / dist;
        pen = BALL_R - dist;
      } else {
        // Ball center inside brick — eject along the closest face axis.
        const left = ball.p.x - b.x;
        const right = (b.x + b.w) - ball.p.x;
        const top = ball.p.y - b.y;
        const bot = (b.y + b.h) - ball.p.y;
        const min = Math.min(left, right, top, bot);
        if (min === left) { nx = -1; ny = 0; }
        else if (min === right) { nx = 1; ny = 0; }
        else if (min === top) { nx = 0; ny = -1; }
        else { nx = 0; ny = 1; }
        pen = BALL_R + min;
      }
      ball.p.x += nx * (pen + 0.05);
      ball.p.y += ny * (pen + 0.05);
      const dotv = ball.v.x * nx + ball.v.y * ny;
      if (dotv < 0) {
        ball.v.x -= 2 * dotv * nx;
        ball.v.y -= 2 * dotv * ny;
      }
      if (b.type === 'mirror') {
        ball.v.x *= 1.08;
        ball.v.y *= 1.08;
      }
    }
    damageBrick(b, game, {
      impactX, impactY, impactVx: preVx, impactVy: preVy,
    });
    if (!willPierce) return;
    // Continue scanning so a fireball can clear multiple HP1 bricks in one frame.
  }
}

function damageBrick(b: Brick, game: Game, hit: BrickHit = {}): void {
  const fromExplosion = hit.fromExplosion ?? false;
  b.hp -= 1;
  b.hitFlash = 1;
  if (!fromExplosion) {
    const prevCombo = game.combo;
    game.combo += 1;
    game.comboTimerMs = COMBO_TIMEOUT_MS;
    game.maxComboSeen = Math.max(game.maxComboSeen, game.combo);
    // keep_combo mission: track when threshold is held.
    if (game.mission && game.mission.active && game.mission.kind === 'keep_combo') {
      if (game.combo >= game.mission.target) game.mission.progress = game.mission.target;
    }
    // Combo milestone celebration
    if (game.combo >= 5 && Math.floor(game.combo / 5) > Math.floor(prevCombo / 5)) {
      const tier = Math.floor(game.combo / 5);
      const phrase = game.combo >= 30
        ? pickPhrase(PHRASES.combo30)
        : game.combo >= 15
          ? pickPhrase(PHRASES.combo15)
          : pickPhrase(PHRASES.combo5);
      pushArcadeText(game, WORLD_W / 2, BRICK_TOP + 40, `${game.combo}× ${phrase}`, '#fbbf24', {
        kind: 'combo', priority: 60, scale: 1.6, life: 1.2,
      });
      game.shake.mag = Math.max(game.shake.mag, 4);
      game.shake.life = Math.max(game.shake.life, 0.25);
      markExciting(game, 'combo_milestone');
      if (game.waveAffix === 'bomb_bloom') seedBombs(game, 2);
      const k = chaosScale(game);
      const n = Math.round(40 + tier * 14 + k * 20);
      game.ps.spawnSparks(game.paddle.cx, PADDLE_Y - 6, COLOR_AMBER, n, 280 + tier * 30);
      game.ps.spawnEmbers(game.paddle.cx, PADDLE_Y - 6, COLOR_AMBER, Math.round(20 + tier * 8), 220);
      game.flashAlpha = Math.max(game.flashAlpha, Math.min(0.45, 0.15 + tier * 0.04));
    }
  }
  const overdriveBonus = isOverdrive(game) ? 1.5 : 1;
  const bountyBonus = b.type === 'bounty' ? 5 : 1;
  const baseMult = (1 + game.scoreMultBonus)
    * (game.elapsedSec < game.score2xUntilSec ? 2 : 1)
    * overdriveBonus
    * bountyBonus;
  const mult = comboMultiplier(game.combo) * baseMult;
  if (b.hp <= 0) {
    b.destroyed = true;
    const baseScore =
      b.type === 'boss' ? 4000
      : b.type === 'bomb' ? 120
      : b.type === 'bounty' ? 200
      : b.type === 'crystal' ? 180
      : b.maxHp >= 2 ? 100
      : b.type === 'glass' ? 150
      : 50;
    const points = Math.round(baseScore * mult);
    game.score += points;
    game.bricksBroken += 1;
    game.bricksSinceDrop += 1;
    game.momentum += BRICK_MOMENTUM;
    spawnBrickBurst(game, b, hit);
    pushText(game, b.x + b.w / 2, b.y + b.h / 2, `+${points}`, brickHueColor(b.hue));
    if (b.type === 'bounty') {
      game.bountyBricksBroken += 1;
      pushArcadeText(game, b.x + b.w / 2, b.y + b.h / 2 - 16, pickPhrase(PHRASES.bountyBrick), '#fbbf24', {
        kind: 'pickup', priority: 60, scale: 1.2, life: 1.0,
      });
    }
    notifyMission(game, 'brick');
    maybeDropPickup(game, b.x + b.w / 2, b.y + b.h);
    game.audio.play('break', 1 + Math.min(1.4, game.combo / 30));
    game.audio.vibe(8);
    if (b.type === 'bomb') explodeBomb(b, game);
    if (b.type === 'crystal') crystalShatter(b, game);
    if (b.type === 'boss') onBossDefeated(b, game);
    if (!fromExplosion && b.type !== 'bomb' && game.chainPopChance > 0) {
      chainPopNeighbors(b, game);
    }
    // Chain-lightning pickup: each remaining charge zaps a nearby brick.
    if (!fromExplosion && game.chainLightningHits > 0) {
      game.chainLightningHits -= 1;
      zapNearestBrick(b, game);
    }
  } else if (!fromExplosion) {
    const points = Math.round(5 * mult);
    game.score += points;
    spawnSparks(game, b.x + b.w / 2, b.y + b.h / 2, brickHueColor(b.hue));
    game.audio.play('brick', 1 + Math.min(1.2, game.combo / 40));
  }
}

function crystalShatter(b: Brick, game: Game): void {
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  const k = chaosScale(game);
  game.ps.spawnSparks(cx, cy, hexToABGR('#67e8f9'), Math.round(60 * k), 360);
  game.ps.spawnEmbers(cx, cy, hexToABGR('#a5f3fc'), Math.round(30 * k), 220);
  game.shockwaves.push({
    p: { x: cx, y: cy }, r: 0, maxR: 90, life: 0.35, maxLife: 0.35, color: '#67e8f9',
  });
  // Drop a guaranteed pickup as the shard "loot".
  if (game.pickups.filter((p) => p.alive).length < MAX_FALLING_PICKUPS
      && game.elapsedSec - game.lastPickupAtSec > 1.0) {
    const pool: PickupKind[] = ['multi', 'score2x', 'momentum', 'magnet', 'prism_split'];
    spawnDirectedPickup(game, pool[(Math.random() * pool.length) | 0] ?? 'multi');
  }
}

function onBossDefeated(b: Brick, game: Game): void {
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  game.flashAlpha = Math.max(game.flashAlpha, 0.75);
  game.shake.mag = Math.max(game.shake.mag, 12);
  game.shake.life = Math.max(game.shake.life, 0.8);
  game.shockwaves.push({
    p: { x: cx, y: cy }, r: 0, maxR: 360, life: 0.9, maxLife: 0.9, color: '#fbbf24',
  });
  game.wells.push({
    p: { x: cx, y: cy }, strength: 3.5, life: 1.4, maxLife: 1.4,
  });
  const k = chaosScale(game);
  game.ps.spawnEmbers(cx, cy, COLOR_AMBER, Math.round(220 * k), 460);
  game.ps.spawnSparks(cx, cy, COLOR_WHITE, Math.round(200 * k), 520);
  game.ps.spawnSparks(cx, cy, hexToABGR('#f87171'), Math.round(120 * k), 460);
  pushArcadeText(game, WORLD_W / 2, cy, 'BOSS DOWN!', '#fbbf24', {
    kind: 'overdrive', priority: 100, scale: 2.0, life: 1.8,
  });
  spawnDirectedPickup(game, 'multi');
  // Guarantee an upgrade offer after the wave clears.
  game.lastUpgradeAtWave = -100;
  game.lastUpgradeAtSec = -100;
  game.bossActive = false;
  game.bossesDefeated += 1;
  game.audio.play('boss_down');
  game.audio.vibe([30, 30, 60]);
}

function zapNearestBrick(from: Brick, game: Game): void {
  let best: Brick | null = null;
  let bestD2 = Infinity;
  const fx = from.x + from.w / 2;
  const fy = from.y + from.h / 2;
  for (const o of game.bricks) {
    if (o.destroyed || o === from) continue;
    const ox = o.x + o.w / 2;
    const oy = o.y + o.h / 2;
    const d2 = (ox - fx) * (ox - fx) + (oy - fy) * (oy - fy);
    if (d2 < bestD2) { bestD2 = d2; best = o; }
  }
  if (!best) return;
  const bx = best.x + best.w / 2;
  const by = best.y + best.h / 2;
  {
    const dx = bx - fx;
    const dy = by - fy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    damageBrick(best, game, {
      fromExplosion: true,
      impactX: bx - (dx / len) * best.w * 0.4,
      impactY: by - (dy / len) * best.h * 0.4,
      impactVx: (dx / len) * 280,
      impactVy: (dy / len) * 280,
    });
  }
  // Visual arc — a thin line of trail particles.
  const steps = 10;
  for (let i = 0; i < steps; i++) {
    const t = i / steps;
    game.ps.emit(
      TYPE_TRAIL,
      fx + (bx - fx) * t + (Math.random() - 0.5) * 2,
      fy + (by - fy) * t + (Math.random() - 0.5) * 2,
      0, 0, 0.25,
      hexToABGR('#67e8f9'), 1,
    );
  }
}

function explodeBomb(b: Brick, game: Game): void {
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  game.audio.play('bomb');
  game.audio.vibe(30);
  game.shockwaves.push({
    p: { x: cx, y: cy }, r: 0, maxR: 90, life: 0.45, maxLife: 0.45, color: '#fbbf24',
  });
  game.wells.push({ p: { x: cx, y: cy }, strength: 1.6, life: 0.7, maxLife: 0.7 });
  game.shake.mag = Math.max(game.shake.mag, 8);
  game.shake.life = Math.max(game.shake.life, 0.35);
  game.flashAlpha = Math.max(game.flashAlpha, 0.4);
  // Bomb explosion: rising embers + hot sparks + ring shatter. All scale
  // with chaos so late-wave bombs feel apocalyptic.
  const k = chaosScale(game);
  game.ps.spawnEmbers(cx, cy, COLOR_AMBER, Math.round(80 * k), 360 + 60 * k);
  game.ps.spawnSparks(cx, cy, COLOR_WHITE, Math.round(50 * k), 420 + 80 * k);
  game.ps.spawnSparks(cx, cy, COLOR_CORAL, Math.round(30 * k), 320);
  game.ps.spawnBrickShatter(cx - 16, cy - 10, 32, 20, COLOR_AMBER, 0.8 + 0.3 * k);
  // Extra outer shell at high chaos.
  if (k > 2) {
    game.ps.spawnSparks(cx, cy, COLOR_AMBER, Math.round(40 * (k - 1)), 560);
  }
  // Only damage the 8 immediate grid neighbors (row/col within ±1).
  for (const other of game.bricks) {
    if (other === b || other.destroyed) continue;
    if (Math.abs(other.row - b.row) > 1) continue;
    if (Math.abs(other.col - b.col) > 1) continue;
    const ox = other.x + other.w / 2;
    const oy = other.y + other.h / 2;
    const dx = ox - cx;
    const dy = oy - cy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    damageBrick(other, game, {
      fromExplosion: true,
      impactX: ox - (dx / len) * other.w * 0.4,
      impactY: oy - (dy / len) * other.h * 0.4,
      impactVx: (dx / len) * 360,
      impactVy: (dy / len) * 360,
    });
  }
}

function chainPopNeighbors(b: Brick, game: Game): void {
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  for (const other of game.bricks) {
    if (other === b || other.destroyed) continue;
    // Manhattan radius 1 — only the four immediate neighbors.
    if (Math.abs(other.row - b.row) + Math.abs(other.col - b.col) !== 1) continue;
    if (Math.random() >= game.chainPopChance) continue;
    const ox = other.x + other.w / 2;
    const oy = other.y + other.h / 2;
    {
      const dx = ox - cx;
      const dy = oy - cy;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      damageBrick(other, game, {
        fromExplosion: true,
        impactX: ox - (dx / len) * other.w * 0.4,
        impactY: oy - (dy / len) * other.h * 0.4,
        impactVx: (dx / len) * 260,
        impactVy: (dy / len) * 260,
      });
    }
    // Chain pop link — a tiny streak of pixels along the connection line.
    const steps = 8;
    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      game.ps.emit(
        TYPE_TRAIL,
        cx + (ox - cx) * t + (Math.random() - 0.5) * 2,
        cy + (oy - cy) * t + (Math.random() - 0.5) * 2,
        0, 0, 0.22, COLOR_TEAL, 1,
      );
    }
  }
}

function brickHueColor(hue: number): string {
  return `hsl(${hue}, 80%, 62%)`;
}

function onWaveCleared(game: Game): void {
  game.score += 250 + 50 * game.wave;
  game.momentum += WAVE_CLEAR_MOMENTUM;
  pushText(game, WORLD_W / 2, BRICK_TOP + 60, `Wave ${game.wave} clear!`, '#fbbf24', 1.6, 1.2);
  game.shockwaves.push({
    p: { x: WORLD_W / 2, y: BRICK_TOP + 80 }, r: 0, maxR: 220,
    life: 0.55, maxLife: 0.55, color: '#5eead4',
  });
  game.wells.push({
    p: { x: WORLD_W / 2, y: BRICK_TOP + 80 },
    strength: 2.4, life: 1.0, maxLife: 1.0,
  });
  game.flashAlpha = Math.max(game.flashAlpha, 0.35);
  game.shake.mag = Math.max(game.shake.mag, 4);
  game.shake.life = Math.max(game.shake.life, 0.3);
  // Wave clear: layered firework. Multi-ring at varying speeds + a wave of
  // pixels sweeping out across the playfield. Density scales with chaos so
  // clearing wave 10 looks completely different from wave 1.
  const cx = WORLD_W / 2;
  const cy = BRICK_TOP + 80;
  const k = chaosScale(game);
  for (const c of CLEAR_PALETTE) {
    game.ps.spawnEmbers(cx, cy, c, Math.round(80 * k), 280 + 40 * k);
  }
  game.ps.spawnSparks(cx, cy, COLOR_WHITE, Math.round(140 * k), 380 + 80 * k);
  // Outer concentric rings of fragments — punches up the firework.
  const rings = Math.min(4, 1 + Math.floor(k));
  for (let r = 0; r < rings; r++) {
    const ringColor = CLEAR_PALETTE[r % CLEAR_PALETTE.length]!;
    const speed = 200 + r * 180;
    const n = Math.round(60 + 30 * k);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + r * 0.4;
      game.ps.emit(
        0, // TYPE_SPARK
        cx, cy,
        Math.cos(a) * speed, Math.sin(a) * speed,
        0.6 + Math.random() * 0.6,
        ringColor, 2,
      );
    }
  }
  game.wave += 1;
  buildWave(game);
  if (game.balls.filter((b) => b.alive).length === 0) spawnServeBall(game);
  if (game.extraBallPerWave) splitBalls(game);
  announceWave(game);
}

function announceWave(game: Game): void {
  if (game.bossActive) {
    pushArcadeText(game, WORLD_W / 2, BRICK_TOP - 28, 'BOSS WAVE', '#f87171', {
      kind: 'overdrive', priority: 100, scale: 1.8, life: 1.8,
    });
    pushArcadeText(game, WORLD_W / 2, BRICK_TOP - 6, 'CRYSTAL HEART', '#fbbf24', {
      kind: 'overdrive', priority: 90, scale: 1.1, life: 1.8,
    });
    return;
  }
  if (game.waveAffix === 'none') return;
  const labels: Record<Exclude<WaveAffix, 'none'>, [string, string]> = {
    comet_rain: ['COMET RAIN', 'Meteors strike the field.'],
    glass_garden: ['GLASS GARDEN', 'Fragile, high-score bricks.'],
    bomb_bloom: ['BOMB BLOOM', 'Combos plant explosives.'],
    moving_row: ['MOVING ROW', 'A row drifts. Aim sharp.'],
    star_rush: ['STAR RUSH', 'Pickups fall fast.'],
  };
  const [name, hint] = labels[game.waveAffix];
  pushArcadeText(game, WORLD_W / 2, BRICK_TOP - 22, `WAVE ${game.wave}: ${name}`, '#67e8f9', {
    kind: 'mission', priority: 85, scale: 1.3, life: 1.6,
  });
  pushArcadeText(game, WORLD_W / 2, BRICK_TOP - 4, hint, '#a5f3fc', {
    kind: 'mission', priority: 75, scale: 0.9, life: 1.6,
  });
}

function spawnSparks(game: Game, x: number, y: number, color: string): void {
  const k = chaosScale(game);
  const c = colorU32(color);
  game.ps.spawnSparks(x, y, c, Math.round(14 * k), 200 + 30 * k);
  // High chaos: a few embers float up from the impact for extra read.
  if (k > 1.8) game.ps.spawnEmbers(x, y, c, Math.round(4 * (k - 1)), 140);
}

function spawnBrickBurst(game: Game, b: Brick, hit: BrickHit): void {
  const k = chaosScale(game);
  const c = brickHueU32(b.hue);
  const bcx = b.x + b.w / 2;
  const bcy = b.y + b.h / 2;
  const impactX = hit.impactX ?? bcx;
  const impactY = hit.impactY ?? bcy;
  const impactVx = hit.impactVx ?? 0;
  const impactVy = hit.impactVy ?? 0;
  // Cell fracture — chunks fly outward from the impact point with rotation.
  // Boss/crystal bricks get high intensity; fireball/overdrive/high-chaos
  // boost the rest from low → mid → high.
  const fireball = game.elapsedSec < game.fireballUntilSec;
  let intensity = INTENSITY_LOW;
  if (k > 1.4) intensity = INTENSITY_MID;
  if (fireball || isOverdrive(game) || k > 2.4 || b.type === 'boss' || b.type === 'crystal') {
    intensity = INTENSITY_HIGH;
  }
  game.chunks.spawnFracture({
    brickX: b.x, brickY: b.y, brickW: b.w, brickH: b.h,
    brickColorABGR: c,
    impactX, impactY, impactVx, impactVy,
    chaos: k, intensity, variant: game.bricksBroken & 3,
  });
  // Hot flash kernel — pure white sparks at impact origin.
  game.ps.spawnSparks(bcx, bcy, COLOR_WHITE, Math.round(6 + 5 * k), 260 + 40 * k);
  // Late game: extra rising embers in brick color for screen flood.
  if (k > 2.2) {
    game.ps.spawnEmbers(bcx, bcy, c, Math.round(8 * (k - 1.5)), 180);
  }
}

function spawnPickupBurst(game: Game, x: number, y: number, color: string): void {
  const k = chaosScale(game);
  const c = colorU32(color);
  game.ps.spawnEmbers(x, y, c, Math.round(28 * k), 200 + 40 * k);
  game.ps.spawnSparks(x, y, COLOR_WHITE, Math.round(10 * k), 240 + 30 * k);
  // Outer ring at high chaos — concentric look.
  if (k > 1.5) game.ps.spawnSparks(x, y, c, Math.round(20 * (k - 1)), 360);
}

function pushText(
  game: Game,
  x: number,
  y: number,
  text: string,
  color: string,
  scale = 1,
  life = 0.9,
): void {
  pushArcadeText(game, x, y, text, color, {
    kind: 'score', priority: 10, scale, life,
  });
}

interface ArcadeTextOpts {
  kind: TextKind;
  priority: number;
  scale?: number;
  life?: number;
}

function pushArcadeText(
  game: Game,
  x: number,
  y: number,
  text: string,
  color: string,
  opts: ArcadeTextOpts,
): void {
  const life = opts.life ?? 0.9;
  const scale = opts.scale ?? 1;
  // Soft cap: when over budget, drop the lowest-priority existing entry so
  // important banners stay readable.
  if (game.texts.length >= MAX_FLOATING_TEXTS) {
    let lowestIdx = -1;
    let lowestPriority = opts.priority;
    for (let i = 0; i < game.texts.length; i++) {
      const t = game.texts[i]!;
      if (t.priority < lowestPriority) {
        lowestPriority = t.priority;
        lowestIdx = i;
      }
    }
    if (lowestIdx < 0) return; // nothing lower than the incoming text
    game.texts.splice(lowestIdx, 1);
  }
  game.texts.push({
    p: { x, y }, vy: -36, life, maxLife: life, text, color, scale,
    kind: opts.kind, priority: opts.priority,
  });
}

// ---- Director ----

function markExciting(game: Game, kind: ExcitingEventKind): void {
  game.director.lastExcitingAtSec = game.elapsedSec;
  if (kind === 'overdrive_started'
      || kind === 'wave_cleared'
      || kind === 'upgrade_offered'
      || kind === 'boss_spawned') {
    game.director.lastMajorAtSec = game.elapsedSec;
  }
}

function stepDirector(game: Game): void {
  if (game.phase !== 'playing') return;
  const now = game.elapsedSec;

  // Guaranteed first pickup — opens the run with a visible win.
  if (!game.firstPickupSpawned && now > GUARANTEED_FIRST_PICKUP_SEC) {
    spawnDirectedPickup(game, 'wide');
    game.firstPickupSpawned = true;
    markExciting(game, 'pickup_spawned');
  }

  const sinceExciting = now - game.director.lastExcitingAtSec;
  const sinceMajor = now - game.director.lastMajorAtSec;
  const sinceMission = now - game.director.lastMissionAtSec;

  // Small event — inject a pickup if we haven't been entertained.
  if (sinceExciting > DRY_SPELL_SMALL_SEC) {
    injectSmallEvent(game);
    markExciting(game, 'pickup_spawned');
  }

  if (sinceMission > MISSION_INTERVAL_SEC && !game.mission?.active) {
    startMission(game);
    game.director.lastMissionAtSec = now;
    markExciting(game, 'mission_started');
  }

  if (sinceMajor > DRY_SPELL_MAJOR_SEC) {
    injectMajorEvent(game);
  }
}

function injectSmallEvent(game: Game): void {
  // Prefer a fresh pickup near the paddle; fallback to a single bomb seed.
  if (game.pickups.filter((p) => p.alive).length < MAX_FALLING_PICKUPS) {
    const pool: PickupKind[] = ['wide', 'multi', 'score2x', 'momentum', 'magnet', 'safe'];
    spawnDirectedPickup(game, pool[(Math.random() * pool.length) | 0] ?? 'wide');
    return;
  }
  seedBombs(game, 1);
}

function injectMajorEvent(game: Game): void {
  if (game.momentum > 0.6) {
    startOverdrive(game);
    return;
  }
  // Else seed a multiball storm via a directed multi pickup + bomb wave.
  seedBombs(game, 2);
  spawnDirectedPickup(game, 'multi');
  game.director.lastMajorAtSec = game.elapsedSec;
}

function spawnDirectedPickup(game: Game, kind: PickupKind): void {
  // Drop it slightly above the paddle column so the player feels the throw.
  const x = clamp(game.paddle.cx + (Math.random() - 0.5) * 80, 24, WORLD_W - 24);
  const y = BRICK_TOP + 40 + Math.random() * 60;
  game.pickups.push({
    p: { x, y },
    v: { x: 0, y: PICKUP_FALL },
    kind,
    alive: true,
    age: 0,
  });
  game.lastPickupAtSec = game.elapsedSec;
}

// ---- Mission ----

function startMission(game: Game): void {
  game.mission = rollMission(game.elapsedSec);
  const m = game.mission;
  pushArcadeText(game, WORLD_W / 2, BRICK_TOP + 20, `${pickPhrase(PHRASES.missionStart)}: ${m.label}`, '#67e8f9', {
    kind: 'mission', priority: 60, scale: 1.15, life: 1.6,
  });
}

function stepMission(game: Game): void {
  const m = game.mission;
  if (!m || !m.active) return;
  if (game.elapsedSec > m.deadlineSec) {
    m.active = false;
    // Soft fail — no penalty, just clear so a new one can roll.
    return;
  }
  if (m.progress >= m.target) completeMission(game);
}

function completeMission(game: Game): void {
  const m = game.mission;
  if (!m || !m.active) return;
  m.active = false;
  game.missionsCompleted += 1;
  markExciting(game, 'mission_completed');
  pushArcadeText(game, WORLD_W / 2, BRICK_TOP + 20, pickPhrase(PHRASES.missionDone), '#fbbf24', {
    kind: 'mission', priority: 70, scale: 1.4, life: 1.6,
  });
  game.flashAlpha = Math.max(game.flashAlpha, 0.3);
  // Reward
  switch (m.reward.kind) {
    case 'pickup':
      spawnDirectedPickup(game, Math.random() < 0.5 ? 'multi' : 'fireball');
      break;
    case 'momentum':
      game.momentum = Math.min(MOMENTUM_TARGET, game.momentum + 0.25);
      break;
    case 'shield':
      game.shields = Math.min(SHIELDS_START + 2, game.shields + 1);
      break;
    case 'score2x':
      game.score2xUntilSec = game.elapsedSec + 8;
      break;
    case 'pulse_ready':
      game.paddle.charge = 1;
      break;
  }
}

function stepAffix(game: Game, dt: number): void {
  if (game.waveAffix === 'none') return;
  game.affixTick += dt;
  if (game.waveAffix === 'comet_rain') {
    if (game.affixTick > 2.6) {
      game.affixTick = 0;
      const alive = game.bricks.filter((b) => !b.destroyed && b.type !== 'boss');
      const pick = alive[(Math.random() * alive.length) | 0];
      if (pick) {
        const sx = pick.x + pick.w / 2;
        const sy = HUD_H;
        // Comet streak particles before impact.
        for (let i = 0; i < 18; i++) {
          const t = i / 18;
          const tx = sx + (Math.random() - 0.5) * 8;
          const ty = sy + (pick.y - sy) * t;
          game.ps.emit(TYPE_TRAIL, tx, ty, 0, 220, 0.35, COLOR_AMBER, 2);
        }
        damageBrick(pick, game, {
          fromExplosion: true,
          impactX: pick.x + pick.w / 2,
          impactY: pick.y,
          impactVx: 0,
          impactVy: 340,
        });
      }
    }
  } else if (game.waveAffix === 'moving_row' && game.movingRowIndex >= 0) {
    const speed = 60;
    game.movingRowOffset += speed * dt;
    const amp = (WORLD_W - (COLS * BRICK_W + (COLS + 1) * BRICK_GAP)) * 0.5 + 24;
    const ox = Math.sin(game.movingRowOffset / 60) * amp;
    for (const b of game.bricks) {
      if (b.destroyed || b.row !== game.movingRowIndex) continue;
      const baseX = BRICK_GAP + b.col * (BRICK_W + BRICK_GAP);
      b.x = baseX + ox;
    }
  }
}

function notifyMission(game: Game, ev: 'brick' | 'perfect' | 'pickup', amount = 1): void {
  const m = game.mission;
  if (!m || !m.active) return;
  if (ev === 'brick' && m.kind === 'break_bricks') m.progress += amount;
  else if (ev === 'perfect' && m.kind === 'perfect_hits') m.progress += amount;
  else if (ev === 'pickup' && m.kind === 'collect_pickups') m.progress += amount;
  else if (m.kind === 'keep_combo') {
    // Updated externally by combo tracker — check current combo against target.
  }
}

// ---- Overdrive ----

function isOverdrive(game: Game): boolean {
  return game.elapsedSec < game.overdriveUntilSec;
}

function startOverdrive(game: Game): void {
  game.momentum = 0;
  game.overdriveCount += 1;
  game.overdriveLevel = 1 + Math.floor(game.overdriveCount / 3);
  game.overdriveUntilSec = game.elapsedSec + OVERDRIVE_DURATION_SEC;
  game.momentumMilestones += 1;
  game.flashAlpha = Math.max(game.flashAlpha, 0.65);
  game.shake.mag = Math.max(game.shake.mag, 9);
  game.shake.life = Math.max(game.shake.life, 0.6);
  game.shockwaves.push({
    p: { x: WORLD_W / 2, y: WORLD_H / 2 }, r: 0, maxR: 320,
    life: 0.8, maxLife: 0.8, color: '#fbbf24',
  });
  game.wells.push({
    p: { x: WORLD_W / 2, y: WORLD_H / 2 },
    strength: 3.2, life: 1.2, maxLife: 1.2,
  });
  seedBombs(game, 3 + game.overdriveLevel);
  // Shower extra pickups during the rush.
  if (game.pickups.filter((p) => p.alive).length < MAX_FALLING_PICKUPS) {
    spawnDirectedPickup(game, 'multi');
  }
  pushArcadeText(game, WORLD_W / 2, WORLD_H * 0.42, pickPhrase(PHRASES.overdrive), '#fbbf24', {
    kind: 'overdrive', priority: 100, scale: 2.1, life: 1.7,
  });
  pushArcadeText(game, WORLD_W / 2, WORLD_H * 0.42 + 22, `Lv ${game.overdriveLevel} · ${OVERDRIVE_DURATION_SEC.toFixed(0)}s`, '#fde68a', {
    kind: 'overdrive', priority: 90, scale: 1.0, life: 1.6,
  });
  markExciting(game, 'overdrive_started');
  game.audio.play('overdrive');
  game.audio.vibe([12, 20, 12, 20, 40]);
}

// ---- Paddle skill ----

function onPerfectPaddleHit(game: Game, ball: Ball, type: 'center' | 'edge'): void {
  game.paddle.perfectStreak += 1;
  game.paddle.lastPerfectAtSec = game.elapsedSec;
  game.perfectHits += 1;
  const gain = type === 'edge' ? 0.32 : 0.22;
  game.paddle.charge = Math.min(1, game.paddle.charge + gain);
  game.comboTimerMs += 600;
  const phrase = type === 'edge'
    ? pickPhrase(PHRASES.perfectEdge)
    : pickPhrase(PHRASES.perfectCenter);
  pushArcadeText(game, ball.p.x, PADDLE_Y - 18, phrase, '#fbbf24', {
    kind: 'perfect', priority: 40, scale: 1.25, life: 0.9,
  });
  game.shockwaves.push({
    p: { x: ball.p.x, y: PADDLE_Y },
    r: 0,
    maxR: type === 'edge' ? 120 : 80,
    life: 0.35, maxLife: 0.35,
    color: type === 'edge' ? '#f87171' : '#fbbf24',
  });
  notifyMission(game, 'perfect');
  game.audio.play('perfect', type === 'edge' ? 1.2 : 1);
  game.audio.vibe(12);

  if (game.paddle.charge >= 1 && game.flags.paddleCannon) {
    firePaddlePulse(game);
  }
}

function firePaddlePulse(game: Game): void {
  if (game.paddle.charge < 1) return;
  game.paddle.charge = 0;
  const beamX = game.paddle.cx;
  for (const b of game.bricks) {
    if (b.destroyed) continue;
    const bx = b.x + b.w / 2;
    if (Math.abs(bx - beamX) < 24) damageBrick(b, game, {
      fromExplosion: true,
      impactX: bx, impactY: b.y + b.h,
      impactVx: 0, impactVy: -260,
    });
  }
  game.shockwaves.push({
    p: { x: beamX, y: BRICK_TOP + 60 }, r: 0, maxR: 200,
    life: 0.45, maxLife: 0.45, color: '#67e8f9',
  });
  game.flashAlpha = Math.max(game.flashAlpha, 0.35);
  pushArcadeText(game, beamX, PADDLE_Y - 40, pickPhrase(PHRASES.pulse), '#67e8f9', {
    kind: 'perfect', priority: 70, scale: 1.4, life: 1.0,
  });
  game.audio.play('pulse');
  game.audio.vibe([10, 10, 20]);
}

function maybeNearMissSave(ball: Ball, game: Game): boolean {
  if (game.elapsedSec - game.lastNearMissSaveAtSec < NEAR_MISS_COOLDOWN_SEC) return false;
  const paddle = game.paddle;
  const dx = Math.abs(ball.p.x - paddle.cx);
  const near = dx < paddle.width / 2 + NEAR_MISS_REACH;
  if (!near) return false;
  game.lastNearMissSaveAtSec = game.elapsedSec;
  ball.p.x = clamp(ball.p.x, paddle.cx - paddle.width / 2 + BALL_R, paddle.cx + paddle.width / 2 - BALL_R);
  ball.p.y = PADDLE_Y - BALL_R - 1;
  ball.v.y = -Math.abs(ball.v.y || INITIAL_BALL_SPEED);
  pushArcadeText(game, ball.p.x, PADDLE_Y - 26, 'MAGNET SAVE!', '#a78bfa', {
    kind: 'save', priority: 80, scale: 1.3, life: 1.0,
  });
  game.shockwaves.push({
    p: { x: ball.p.x, y: PADDLE_Y },
    r: 0, maxR: 90, life: 0.35, maxLife: 0.35, color: '#a78bfa',
  });
  return true;
}

// ---- Shield recovery blast ----

function shieldRecoveryBlast(game: Game): void {
  game.audio.play('save');
  game.audio.vibe([20, 30, 30]);
  const candidates = game.bricks
    .filter((b) => !b.destroyed)
    .sort((a, b) => b.y - a.y)
    .slice(0, 8);
  const count = clamp(3 + (game.flags.shieldBloom ? 3 : 0), 3, 8);
  for (const b of candidates.slice(0, count)) {
    damageBrick(b, game, {
      fromExplosion: true,
      impactX: b.x + b.w / 2,
      impactY: b.y + b.h,
      impactVx: 0,
      impactVy: -220,
    });
  }
  spawnDirectedPickup(game, Math.random() < 0.5 ? 'safe' : 'slow');
  game.shockwaves.push({
    p: { x: game.paddle.cx, y: PADDLE_Y },
    r: 0, maxR: 260, life: 0.7, maxLife: 0.7, color: '#a78bfa',
  });
  game.flashAlpha = Math.max(game.flashAlpha, 0.45);
  pushArcadeText(game, game.paddle.cx, PADDLE_Y - 32, pickPhrase(PHRASES.save), '#a78bfa', {
    kind: 'save', priority: 90, scale: 1.5, life: 1.2,
  });
}

// Reusable grid buffer to avoid per-frame allocation.
const _gridX = new Float32Array((GRID_COLS + 1) * (GRID_ROWS + 1));
const _gridY = new Float32Array((GRID_COLS + 1) * (GRID_ROWS + 1));
const _gridMag = new Float32Array((GRID_COLS + 1) * (GRID_ROWS + 1));

function drawWarpGrid(ctx: CanvasRenderingContext2D, game: Game): void {
  const cellW = WORLD_W / GRID_COLS;
  const cellH = (WORLD_H - HUD_H) / GRID_ROWS;
  const stride = GRID_COLS + 1;
  // Compute displaced positions
  for (let r = 0; r <= GRID_ROWS; r++) {
    for (let c = 0; c <= GRID_COLS; c++) {
      const baseX = c * cellW;
      const baseY = HUD_H + r * cellH;
      let dx = 0;
      let dy = 0;
      // Ball pulls (gentle, 1/r falloff)
      for (const ball of game.balls) {
        if (!ball.alive) continue;
        const ex = baseX - ball.p.x;
        const ey = baseY - ball.p.y;
        const d2 = ex * ex + ey * ey + 800;
        const k = -GRID_BALL_PULL / d2;
        dx += ex * k;
        dy += ey * k;
      }
      // Well pulls (strong, decay with life)
      for (const w of game.wells) {
        const t = w.life / w.maxLife;
        const ex = baseX - w.p.x;
        const ey = baseY - w.p.y;
        const d2 = ex * ex + ey * ey + 600;
        const k = -GRID_WELL_PULL * w.strength * t / d2;
        dx += ex * k;
        dy += ey * k;
      }
      // Cap displacement so grid stays readable
      const dmag = Math.sqrt(dx * dx + dy * dy);
      if (dmag > GRID_MAX_DISP) {
        const s = GRID_MAX_DISP / dmag;
        dx *= s; dy *= s;
      }
      const idx = r * stride + c;
      _gridX[idx] = baseX + dx;
      _gridY[idx] = baseY + dy;
      _gridMag[idx] = Math.min(1, dmag / GRID_MAX_DISP);
    }
  }

  // Draw lines
  ctx.lineWidth = 0.6;
  for (let r = 0; r <= GRID_ROWS; r++) {
    for (let c = 0; c <= GRID_COLS; c++) {
      const i = r * stride + c;
      const x = _gridX[i] ?? 0;
      const y = _gridY[i] ?? 0;
      const mag = _gridMag[i] ?? 0;
      if (c < GRID_COLS) {
        const j = i + 1;
        const x2 = _gridX[j] ?? 0;
        const y2 = _gridY[j] ?? 0;
        const mag2 = _gridMag[j] ?? 0;
        const m = Math.max(mag, mag2);
        const alpha = 0.07 + m * 0.55;
        ctx.strokeStyle = `rgba(94,234,212,${alpha})`;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
      if (r < GRID_ROWS) {
        const j = i + stride;
        const x2 = _gridX[j] ?? 0;
        const y2 = _gridY[j] ?? 0;
        const mag2 = _gridMag[j] ?? 0;
        const m = Math.max(mag, mag2);
        const alpha = 0.07 + m * 0.55;
        ctx.strokeStyle = `rgba(103,232,249,${alpha})`;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    }
  }
}

function render(
  ctx: CanvasRenderingContext2D,
  game: Game,
  particleCanvas: HTMLCanvasElement,
  chunkDt: number,
): void {
  const cssW = ctx.canvas.clientWidth;
  const cssH = ctx.canvas.clientHeight;
  const dpr = Math.max(1, ctx.canvas.width / Math.max(1, cssW));
  const sx = cssW / WORLD_W;
  const sy = cssH / WORLD_H;
  const s = Math.min(sx, sy);
  const ox = (cssW - WORLD_W * s) / 2;
  const oy = (cssH - WORLD_H * s) / 2;

  ctx.save();
  ctx.fillStyle = '#0b0d13';
  ctx.fillRect(0, 0, cssW, cssH);

  // Backdrop gradient inside playfield (with screen shake offset)
  ctx.translate(ox + game.shake.x * s, oy + game.shake.y * s);
  ctx.scale(s, s);

  const grad = ctx.createRadialGradient(WORLD_W * 0.7, -40, 60, WORLD_W / 2, WORLD_H / 2, WORLD_H);
  grad.addColorStop(0, 'rgba(94,234,212,0.10)');
  grad.addColorStop(0.4, 'rgba(103,232,249,0.04)');
  grad.addColorStop(1, 'rgba(11,13,19,1)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, WORLD_W, WORLD_H);

  // Warp grid (geometry-wars style)
  drawWarpGrid(ctx, game);

  // Top playfield boundary
  ctx.fillStyle = 'rgba(255,255,255,0.04)';
  ctx.fillRect(0, HUD_H, WORLD_W, 1);

  // Shockwaves (under bricks)
  for (const sw of game.shockwaves) {
    const t = 1 - sw.life / sw.maxLife;
    ctx.globalAlpha = (1 - t) * 0.7;
    ctx.strokeStyle = sw.color;
    ctx.lineWidth = 4 - t * 3;
    ctx.beginPath();
    ctx.arc(sw.p.x, sw.p.y, sw.r, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.lineWidth = 1;

  // Bricks
  for (const b of game.bricks) {
    if (b.destroyed) continue;
    drawBrick(ctx, b);
  }

  // Cell-fracture chunks — fused step+render. Internally uses absolute
  // setTransform that drops the outer dpr factor; wrap in save/restore so
  // subsequent world-space draws keep the dpr·world matrix.
  ctx.save();
  game.chunks.stepAndRender(chunkDt, ctx, {
    paddleCx: game.paddle.cx,
    paddleHalfW: game.paddle.width / 2,
    paddleTopY: PADDLE_Y,
    paddleH: PADDLE_H,
    groundY: WORLD_H - 4,
    worldH: WORLD_H,
    canvasScale: s * dpr,
    canvasOffsetX: (ox + game.shake.x * s) * dpr,
    canvasOffsetY: (oy + game.shake.y * s) * dpr,
  });
  ctx.restore();

  // Pickups
  for (const pu of game.pickups) drawPickup(ctx, pu);

  // Particles — pre-rendered offscreen layer (device pixels), composited with
  // 'lighter' for additive glow. Step out of world transform first and reset
  // alpha so dim leftover state from earlier draws can't kill the layer.
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'lighter';
  ctx.drawImage(particleCanvas, 0, 0);
  ctx.restore();

  // Balls + trails
  for (const ball of game.balls) {
    if (!ball.alive) continue;
    for (let i = 0; i < ball.trail.length; i++) {
      const t = ball.trail[i];
      if (!t) continue;
      const a = (i / ball.trail.length) * 0.45;
      ctx.globalAlpha = a;
      ctx.fillStyle = '#67e8f9';
      ctx.beginPath();
      ctx.arc(t.x, t.y, BALL_R * (0.4 + (i / ball.trail.length) * 0.6), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    const g = ctx.createRadialGradient(ball.p.x, ball.p.y, 0, ball.p.x, ball.p.y, BALL_R * 2.5);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(0.4, '#67e8f9');
    g.addColorStop(1, 'rgba(94,234,212,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(ball.p.x, ball.p.y, BALL_R * 2.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(ball.p.x, ball.p.y, BALL_R, 0, Math.PI * 2);
    ctx.fill();
  }

  // Paddle
  drawPaddle(ctx, game.paddle);

  // Floating text (with scale + glow)
  for (const t of game.texts) {
    const alpha = Math.max(0, t.life / t.maxLife);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(t.p.x, t.p.y);
    const grow = 1 + (1 - alpha) * 0.4;
    ctx.scale(t.scale * grow, t.scale * grow);
    ctx.font = 'bold 14px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.shadowColor = t.color;
    ctx.shadowBlur = 12;
    ctx.fillStyle = t.color;
    ctx.fillText(t.text, 0, 0);
    ctx.restore();
  }
  ctx.globalAlpha = 1;

  // Serve countdown — big in-canvas number with pulse
  if (game.phase === 'serving' && game.serveCountdownUntilSec !== null) {
    const remain = game.serveCountdownUntilSec - game.elapsedSec;
    if (remain > 0) {
      const wholeRemain = Math.ceil(remain);
      const frac = remain - (wholeRemain - 1); // 1 → 0 within current second
      const popIn = 1 - Math.pow(frac, 2); // emphasizes the tick instant
      const scale = 1.4 + popIn * 0.9;
      const alpha = 0.6 + popIn * 0.4;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(WORLD_W / 2, WORLD_H * 0.45);
      ctx.scale(scale, scale);
      ctx.font = 'bold 64px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = '#5eead4';
      ctx.shadowBlur = 28;
      ctx.fillStyle = '#ffffff';
      ctx.fillText(String(wholeRemain), 0, 0);
      ctx.shadowBlur = 0;
      ctx.font = 'bold 11px Inter, system-ui, sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.65)';
      ctx.fillText('TAP TO LAUNCH', 0, 28);
      ctx.restore();
    }
  }

  // In-game HUD additions (charge bar, mission chip, overdrive border).
  renderInGameHud(ctx, game);

  // Canvas menus (start, pause) — drawn over gameplay/HUD, under flash.
  if (game.menu) renderCanvasMenu(ctx, game);

  // Full-screen flash overlay
  if (game.flashAlpha > 0.01) {
    ctx.globalAlpha = game.flashAlpha;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, WORLD_W, WORLD_H);
    ctx.globalAlpha = 1;
  }

  ctx.restore();
}

function renderInGameHud(ctx: CanvasRenderingContext2D, game: Game): void {
  const t = game.elapsedSec;

  // Overdrive ring: gold border that pulses while active.
  if (isOverdrive(game)) {
    const remain = game.overdriveUntilSec - t;
    const pulse = 0.6 + 0.4 * Math.sin(t * 8);
    ctx.save();
    ctx.strokeStyle = `rgba(251,191,36,${0.7 * pulse})`;
    ctx.lineWidth = 4;
    ctx.strokeRect(2, HUD_H + 2, WORLD_W - 4, WORLD_H - HUD_H - 4);
    ctx.fillStyle = '#fbbf24';
    ctx.font = 'bold 11px Inter, system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`OVERDRIVE ${remain.toFixed(1)}s`, WORLD_W - 8, HUD_H + 16);
    ctx.restore();
  }

  // Paddle charge bar (above paddle).
  if (game.flags.paddleCannon || game.paddle.charge > 0) {
    const cw = 60;
    const cx = game.paddle.cx;
    const cy = PADDLE_Y - 10;
    ctx.save();
    ctx.fillStyle = 'rgba(8,12,20,0.7)';
    ctx.fillRect(cx - cw / 2, cy, cw, 3);
    const filled = cw * game.paddle.charge;
    ctx.fillStyle = game.paddle.charge >= 1 ? '#67e8f9' : '#5eead4';
    ctx.fillRect(cx - cw / 2, cy, filled, 3);
    if (game.paddle.charge >= 1) {
      const blink = 0.5 + 0.5 * Math.sin(t * 12);
      ctx.fillStyle = `rgba(103,232,249,${0.4 + blink * 0.5})`;
      ctx.font = 'bold 9px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('PULSE READY', cx, cy - 4);
    }
    ctx.restore();
  }

  // Mission chip — sits above the play area in the canvas's top buffer.
  const m = game.mission;
  if (m && m.active) {
    const remain = Math.max(0, m.deadlineSec - t);
    const w = 220;
    const h = 20;
    const x = (WORLD_W - w) / 2;
    const y = 18;
    ctx.save();
    ctx.fillStyle = 'rgba(8,12,20,0.85)';
    roundRect(ctx, x, y, w, h, 6);
    ctx.fill();
    ctx.strokeStyle = '#67e8f9';
    ctx.lineWidth = 1;
    roundRect(ctx, x, y, w, h, 6);
    ctx.stroke();
    ctx.fillStyle = '#67e8f9';
    ctx.font = 'bold 10px Inter, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(m.label, x + 8, y + h / 2);
    const progressText = m.kind === 'keep_combo'
      ? ''
      : `${Math.min(m.progress, m.target)}/${m.target}`;
    ctx.textAlign = 'right';
    ctx.fillText(progressText, x + w - 8, y + h / 2);
    // Timer bar under chip.
    const tw = w * (remain / Math.max(0.1, m.deadlineSec - m.startedAtSec));
    ctx.fillStyle = 'rgba(103,232,249,0.55)';
    ctx.fillRect(x, y + h + 1, tw, 2);
    ctx.restore();
  }

  // Session arc indicator (small bar top-right).
  if (!game.sessionComplete) {
    const pct = clamp(game.elapsedSec / SESSION_TARGET_SEC, 0, 1);
    const w = 80;
    const h = 3;
    const x = WORLD_W - w - 8;
    const y = HUD_H - 8;
    ctx.save();
    ctx.fillStyle = 'rgba(94,234,212,0.18)';
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = '#5eead4';
    ctx.fillRect(x, y, w * pct, h);
    ctx.restore();
  }
}

function drawBrick(ctx: CanvasRenderingContext2D, b: Brick): void {
  const r = b.type === 'boss' ? 8 : 4;
  const flash = b.hitFlash;
  const baseColor = brickHueColor(b.hue);
  let grad: CanvasGradient;
  if (b.type === 'boss') {
    grad = ctx.createLinearGradient(b.x, b.y, b.x, b.y + b.h);
    grad.addColorStop(0, '#fbbf24');
    grad.addColorStop(0.5, '#f87171');
    grad.addColorStop(1, '#7c1d1d');
  } else if (b.type === 'mirror') {
    grad = ctx.createLinearGradient(b.x, b.y, b.x + b.w, b.y + b.h);
    grad.addColorStop(0, '#e5e7eb');
    grad.addColorStop(0.5, '#94a3b8');
    grad.addColorStop(1, '#e5e7eb');
  } else {
    grad = ctx.createLinearGradient(b.x, b.y, b.x, b.y + b.h);
    grad.addColorStop(0, `hsl(${(b.hue + 6) % 360}, 80%, 70%)`);
    grad.addColorStop(1, `hsl(${b.hue}, 70%, 48%)`);
  }
  ctx.save();
  ctx.shadowColor = b.type === 'boss' ? '#fbbf24' : b.type === 'mirror' ? '#cbd5e1' : baseColor;
  ctx.shadowBlur = (b.type === 'boss' ? 18 : 8) + flash * 12;
  roundRect(ctx, b.x, b.y, b.w, b.h, r);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.shadowBlur = 0;
  // Crystal: facet overlay
  if (b.type === 'crystal') {
    ctx.globalAlpha = 0.5;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(b.x + b.w / 2, b.y + 2);
    ctx.lineTo(b.x + b.w - 2, b.y + b.h / 2);
    ctx.lineTo(b.x + b.w / 2, b.y + b.h - 2);
    ctx.lineTo(b.x + 2, b.y + b.h / 2);
    ctx.closePath();
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
  // Mirror: glossy highlight stripe
  if (b.type === 'mirror') {
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(b.x + 2, b.y + 2, b.w - 4, 3);
    ctx.globalAlpha = 1;
  }
  // Boss: HP bar overlay + pulsing core
  if (b.type === 'boss') {
    const pct = clamp(b.hp / Math.max(1, b.maxHp), 0, 1);
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(b.x + 6, b.y + b.h - 8, b.w - 12, 4);
    ctx.fillStyle = pct > 0.5 ? '#fbbf24' : pct > 0.25 ? '#f97316' : '#dc2626';
    ctx.fillRect(b.x + 6, b.y + b.h - 8, (b.w - 12) * pct, 4);
    const t = performance.now() / 200;
    const pulse = 0.6 + 0.4 * Math.sin(t);
    ctx.fillStyle = `rgba(255,255,255,${0.55 + pulse * 0.35})`;
    ctx.beginPath();
    ctx.arc(b.x + b.w / 2, b.y + b.h / 2 - 4, 5 + pulse * 2, 0, Math.PI * 2);
    ctx.fill();
  }
  // Glass: translucent overlay
  if (b.type === 'glass') {
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = '#ffffff';
    roundRect(ctx, b.x + 2, b.y + 2, b.w - 4, b.h - 4, r - 1);
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  // Reinforced: inner border
  if (b.type === 'reinforced' && b.hp >= 2) {
    ctx.strokeStyle = 'rgba(255,255,255,0.75)';
    ctx.lineWidth = 1.5;
    roundRect(ctx, b.x + 3, b.y + 3, b.w - 6, b.h - 6, r - 2);
    ctx.stroke();
  }
  // Bomb: pulsing dot core + warning ring
  if (b.type === 'bomb') {
    const pulse = 0.6 + 0.4 * Math.sin(performance.now() / 180);
    ctx.fillStyle = `rgba(255,255,255,${0.55 + pulse * 0.35})`;
    ctx.beginPath();
    ctx.arc(b.x + b.w / 2, b.y + b.h / 2, 3.2 + pulse * 1.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = `rgba(251,191,36,${0.6 + pulse * 0.4})`;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(b.x + b.w / 2, b.y + b.h / 2, 6 + pulse * 1.5, 0, Math.PI * 2);
    ctx.stroke();
  }
  // Hit flash overlay
  if (flash > 0) {
    ctx.globalAlpha = flash * 0.6;
    ctx.fillStyle = '#ffffff';
    roundRect(ctx, b.x, b.y, b.w, b.h, r);
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  ctx.restore();
}

function drawPaddle(ctx: CanvasRenderingContext2D, p: Paddle): void {
  const left = p.cx - p.width / 2;
  const top = PADDLE_Y;
  ctx.save();
  ctx.shadowColor = '#5eead4';
  ctx.shadowBlur = 12 + p.glow * 18;
  const grad = ctx.createLinearGradient(0, top, 0, top + PADDLE_H);
  grad.addColorStop(0, '#5eead4');
  grad.addColorStop(1, '#0d9488');
  ctx.fillStyle = grad;
  roundRect(ctx, left, top, p.width, PADDLE_H, 7);
  ctx.fill();
  ctx.restore();
}

function drawPickup(ctx: CanvasRenderingContext2D, pu: Pickup): void {
  const color = pickupColor(pu.kind);
  ctx.save();
  ctx.shadowColor = color;
  ctx.shadowBlur = 14;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(pu.p.x, pu.p.y, 9, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = '#06161a';
  ctx.font = 'bold 9px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(pickupLabel(pu.kind), pu.p.x, pu.p.y + 1);
  ctx.restore();
}

const ALL_UPGRADES: UpgradeDef[] = [
  {
    id: 'wider_base',
    name: 'Wider Base',
    desc: 'Permanent +10% paddle width (cap 130).',
    apply: (g) => { g.paddle.baseWidth = Math.min(130, g.paddle.baseWidth * 1.10); },
    stackable: true,
    canOffer: (g) => g.paddle.baseWidth < 130,
  },
  {
    id: 'momentum_carry',
    name: 'Momentum Carry',
    desc: '+10% momentum right now.',
    apply: (g) => { g.momentum = Math.min(MOMENTUM_TARGET, g.momentum + 0.1); },
  },
  {
    id: 'chain_pop',
    name: 'Chain Pop',
    desc: '+2% chance per neighbor to chain (cap 10%).',
    apply: (g) => { g.chainPopChance = Math.min(0.10, g.chainPopChance + 0.02); },
    stackable: true,
    canOffer: (g) => g.chainPopChance < 0.10,
  },
  {
    id: 'lucky_drops',
    name: 'Lucky Drops',
    desc: 'Pickup chance +3% (cap +6%).',
    apply: (g) => { g.pickupChanceBonus = Math.min(0.06, g.pickupChanceBonus + 0.03); },
    stackable: true,
    canOffer: (g) => g.pickupChanceBonus < 0.06,
  },
  {
    id: 'combo_insurance',
    name: 'Combo Insurance',
    desc: 'Next missed ball keeps your combo.',
    apply: (g) => { g.comboInsurance = true; },
    oneShot: true,
  },
  {
    id: 'extra_ball',
    name: 'Wave Multiball',
    desc: 'Each new wave spawns extra balls.',
    apply: (g) => { g.extraBallPerWave = true; },
  },
  {
    id: 'score_surge',
    name: 'Score Surge',
    desc: 'Permanent +15% brick score (cap +60%).',
    apply: (g) => { g.scoreMultBonus = Math.min(0.6, g.scoreMultBonus + 0.15); },
    stackable: true,
    canOffer: (g) => g.scoreMultBonus < 0.6,
  },
  {
    id: 'shield_charm',
    name: 'Shield Charm',
    desc: 'Restore +1 shield right now.',
    apply: (g) => { g.shields = Math.min(SHIELDS_START + 3, g.shields + 1); },
    oneShot: true,
  },
  {
    id: 'comet_core',
    name: 'Comet Core',
    desc: 'Every 5th hit pierces weak bricks. Stacks lower the count.',
    apply: (g) => { g.cometEvery = g.cometEvery === 0 ? 5 : Math.max(3, g.cometEvery - 1); },
    stackable: true,
    canOffer: (g) => g.cometEvery === 0 || g.cometEvery > 3,
  },
  {
    id: 'shield_bloom',
    name: 'Shield Bloom',
    desc: 'Shield saves blast more lower-grid bricks.',
    apply: (g) => { g.flags.shieldBloom = true; },
    canOffer: (g) => !g.flags.shieldBloom,
  },
  {
    id: 'bounty_hunter',
    name: 'Bounty Hunter',
    desc: 'Each new wave marks a gold brick worth 5× score.',
    apply: (g) => { g.flags.bountyBricks = true; },
    canOffer: (g) => !g.flags.bountyBricks,
  },
  {
    id: 'paddle_cannon',
    name: 'Paddle Cannon',
    desc: 'Perfect hits charge a beam. Auto-fires when full.',
    apply: (g) => { g.flags.paddleCannon = true; },
    canOffer: (g) => !g.flags.paddleCannon,
  },
  {
    id: 'magnet_hands',
    name: 'Magnet Hands',
    desc: 'Pickups always drift toward the paddle a bit.',
    apply: (g) => { g.flags.magnetHands = true; },
    canOffer: (g) => !g.flags.magnetHands,
  },
  {
    id: 'time_thread',
    name: 'Time Thread',
    desc: 'Combo timer decays 30% slower.',
    apply: (g) => { g.flags.timeThread = true; },
    canOffer: (g) => !g.flags.timeThread,
  },
];

function pickUpgradeChoices(game: Game, count: number): UpgradeDef[] {
  const candidates = ALL_UPGRADES.filter((u) => {
    if (u.canOffer && !u.canOffer(game)) return false;
    if (u.oneShot) return true;
    if (u.stackable) return true;
    return !game.upgrades.has(u.id);
  });
  const shuffled = candidates.slice();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = shuffled[i]!;
    const b = shuffled[j]!;
    shuffled[i] = b; shuffled[j] = a;
  }
  return shuffled.slice(0, count);
}

function renderCanvasMenu(ctx: CanvasRenderingContext2D, game: Game): void {
  const m = game.menu;
  if (!m) return;
  const t = game.elapsedSec - m.enteredAtSec;

  // Dim playfield (keep HUD legible).
  ctx.save();
  ctx.fillStyle = 'rgba(3,6,14,0.72)';
  ctx.fillRect(0, HUD_H, WORLD_W, WORLD_H - HUD_H);

  // Panel — translate so panel, title, info, and buttons breathe together.
  const wobble = Math.sin(t * 3) * 1.5;
  ctx.translate(0, wobble);
  const panel: Rect = { x: 22, y: 88, w: WORLD_W - 44, h: WORLD_H - 88 - 22 };
  drawNeonPanel(ctx, panel, m.kind, t);

  // Title
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 22px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = '#5eead4';
  ctx.shadowBlur = 20;
  ctx.fillText(m.title, WORLD_W / 2, panel.y + 38);
  ctx.shadowBlur = 0;
  ctx.font = '500 12px Inter, system-ui, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.fillText(m.subtitle, WORLD_W / 2, panel.y + 62);

  // Info row (best score etc.)
  if (m.info.length > 0) {
    ctx.font = 'bold 11px Inter, system-ui, sans-serif';
    ctx.fillStyle = '#fbbf24';
    const colW = panel.w / m.info.length;
    m.info.forEach((info, i) => {
      ctx.fillText(info, panel.x + colW * i + colW / 2, panel.y + 92);
    });
  }

  // Buttons
  for (const b of m.buttons) drawCanvasButton(ctx, b, t);

  ctx.restore();
}

function drawNeonPanel(ctx: CanvasRenderingContext2D, r: Rect, kind: CanvasMenuKind, t: number): void {
  const pulse = 0.6 + 0.4 * Math.sin(t * 2);
  const stroke = kind === 'pause' ? '#67e8f9' : '#5eead4';
  ctx.save();
  ctx.fillStyle = 'rgba(8,12,22,0.92)';
  roundRect(ctx, r.x, r.y, r.w, r.h, 14);
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1.5;
  ctx.shadowColor = stroke;
  ctx.shadowBlur = 14 * pulse;
  roundRect(ctx, r.x, r.y, r.w, r.h, 14);
  ctx.stroke();
  ctx.restore();
}

function drawCanvasButton(ctx: CanvasRenderingContext2D, b: CanvasButton, t: number): void {
  const pulse = 0.5 + 0.5 * Math.sin(t * 3 + b.rect.x);
  const tone = b.tone;
  const fill = tone === 'primary' ? '#5eead4'
    : tone === 'gold' ? '#fbbf24'
    : tone === 'danger' ? 'rgba(248,113,113,0.18)'
    : 'rgba(94,234,212,0.08)';
  const stroke = tone === 'primary' ? '#5eead4'
    : tone === 'gold' ? '#fbbf24'
    : tone === 'danger' ? '#f87171'
    : 'rgba(94,234,212,0.55)';
  const text = tone === 'primary' || tone === 'gold' ? '#06161a' : '#ffffff';
  const sub = tone === 'primary' || tone === 'gold' ? 'rgba(6,22,26,0.7)' : 'rgba(255,255,255,0.55)';
  ctx.save();
  ctx.fillStyle = fill;
  roundRect(ctx, b.rect.x, b.rect.y, b.rect.w, b.rect.h, 10);
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1.5;
  if (tone === 'primary') {
    ctx.shadowColor = stroke;
    ctx.shadowBlur = 10 + pulse * 8;
  }
  roundRect(ctx, b.rect.x, b.rect.y, b.rect.w, b.rect.h, 10);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.fillStyle = text;
  ctx.font = 'bold 14px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(b.label, b.rect.x + b.rect.w / 2, b.rect.y + b.rect.h / 2 - 7);
  ctx.font = '500 10px Inter, system-ui, sans-serif';
  ctx.fillStyle = sub;
  ctx.fillText(b.sublabel, b.rect.x + b.rect.w / 2, b.rect.y + b.rect.h / 2 + 10);
  ctx.restore();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
  ctx.fill();
}
